import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import liveChatApi, {
  centralChatTicketsEnabled,
  createLiveChatSocketTicket,
  liveChatWsBaseUrl,
  uploadLiveChatPhoto,
} from '../../services/liveChatApi';
import ChatPlayerSearch, { useChatPlayerSearch } from './ChatPlayerSearch';
import { normalizeChatSearch, openDirectoryPlayerChat, unmatchedChatPlayers } from './chatPlayerDirectory';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Bubble, BubbleContent, BubbleGroup, BubbleReactions } from '../../components/ui/bubble';
import { Marker, MarkerContent } from '../../components/ui/marker';
import { Message, MessageAvatar, MessageContent, MessageFooter } from '../../components/ui/message';
import { ArrowLeft, Bot, Camera, ChevronDown, Copy, Filter, Image as ImageIcon, Megaphone, Menu, MessageCircle, MoreHorizontal, Pencil, RefreshCw, Reply, Send, SmilePlus, Star, StarOff, Trash2, UserPlus, UserRound, Zap, Search, Wifi, WifiOff, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import { getStoredSessionToken } from '../../services/sessionTokens';
import AdminAgentChatPanel from './AdminAgentChatPanel';
import useChatComposer from '../../hooks/useChatComposer';
import { OPEN_ADMIN_WORKSPACE_MENU_EVENT } from './adminWorkspacePolicy';
import { configuredChatAgents, staffReadAcknowledgement } from '../../services/staffChatSync';
import { createStaffUnreadState, confirmedStaffUnread } from '../../services/staffChatUnread';
import { sourceChatPlayerIdentity } from '../../services/chatSourceIdentity';
import useChatActor from '../../hooks/useChatActor';
import { ownsChatMessage } from '../../services/chatMessageOwnership';

const IPHONE_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😍', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️',
  '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰',
  '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱',
  '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👻', '💀',
  '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️',
  '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💪', '🫶', '❤️', '🧡', '💛', '💚',
  '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🔥', '💯', '✨',
  '⭐', '🌟', '💫', '🎉', '🎊', '🎁', '🏆', '🥇', '⚡', '💥', '💦', '💨', '👀', '🧠', '💎', '🍀', '🌹', '🌈'
];
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏', '🔥'];
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const LINK_RE = /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,!?;:'"\]])/gi;
const LOCAL_DEMO_TOKEN = 'demo_staff_chat';
const LOCAL_DEMO_CHAT_KEY = 'white_room_demo_chat_messages';
const LOCAL_DEMO_CHAT_EVENT = 'white-room-demo-chat-updated';
const LOCAL_DEMO_BRIDGE_URL = 'http://127.0.0.1:3020/messages';
const ROOM_FETCH_LIMIT = 160;
const MESSAGE_PAGE_LIMIT = 120;
const MESSAGE_MEMORY_LIMIT = 2500;
const MAX_ROOM_LABELS = 5;
const MAX_ROOM_LABEL_LENGTH = 28;
const CHAT_RECOVERY_DELAYS_MS = [1000, 2000, 4000, 8000, 12000];

const isTransientChatLoadError = (error) => {
  const status = Number(error?.response?.status || 0);
  return !status || status === 408 || status === 425 || status === 429 || status >= 500;
};

const chatLoadErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  return 'Your chats could not be loaded. Check your connection and try again.';
};

const CHAT_MESSAGE_TEXT_STYLE = {
  width: '100%',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  hyphens: 'auto',
  letterSpacing: 0,
};

const normalizeRoomLabels = (labels = []) => {
  if (!Array.isArray(labels)) return [];
  const seen = new Set();
  const clean = [];
  labels.forEach((raw) => {
    const label = String(raw || '').replace(/\s+/g, ' ').trim().slice(0, MAX_ROOM_LABEL_LENGTH).trim();
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    clean.push(label);
  });
  return clean.slice(0, MAX_ROOM_LABELS);
};

const roomLabels = (room = {}) => normalizeRoomLabels(room.labels || room.chat_labels || []);

const isLocalDemoChat = () => (
  typeof window !== 'undefined'
  && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
  && getStoredSessionToken({ tenant: 'demo', portal: 'admin' }) === LOCAL_DEMO_TOKEN
);

const demoTimestamp = (minutesAgo = 0) => new Date(Date.now() - minutesAgo * 60000).toISOString();
const DEMO_ROOM = {
  id: 'demo-conversation-1',
  tenant_id: 'demo-tenant',
  player_id: 'demo-player-1',
  player_name: 'emilybrownfi7546',
  player_email: 'emilybrownfield38282@gmail.com',
  chat_display_name: 'Emily Brown',
  player_avatar_url: '',
  subject: 'Texted to agent ANNA',
  status: 'open',
  staff_unread: 1,
  player_online: true,
  last_message: 'Give me you jw username',
  last_sender_role: 'player',
  last_sender_name: 'emilybrownfi7546',
  last_message_at: demoTimestamp(1),
  updated_at: demoTimestamp(1),
  preferred_agent_name: 'ANNA',
  preferred_agent_title: 'Live Agent',
  preferred_agent_bio: '',
};
const DEMO_DRAFT = {
  id: 'demo-ai-draft-1',
  status: 'ready',
  draft_text: 'Sure, please send your Juwa username here. I will check it and guide you from there.',
};

const defaultDemoMessages = () => [
  {
    id: 'demo-msg-1',
    conversation_id: DEMO_ROOM.id,
    sender_id: 'demo-player-1',
    sender_name: 'emilybrownfi7546',
    sender_role: 'player',
    body: 'Hello',
    attachments: [],
    created_at: demoTimestamp(12),
  },
  {
    id: 'demo-msg-2',
    conversation_id: DEMO_ROOM.id,
    sender_id: 'demo-staff-chat',
    sender_name: 'ANNA',
    sender_role: 'staff',
    body: 'Hello you wanna play',
    attachments: [],
    created_at: demoTimestamp(10),
    delivery_status: 'seen',
    target_agent_name: 'ANNA',
    target_agent_title: 'Live Agent',
  },
  {
    id: 'demo-msg-3',
    conversation_id: DEMO_ROOM.id,
    sender_id: 'demo-player-1',
    sender_name: 'emilybrownfi7546',
    sender_role: 'player',
    body: 'Yes',
    attachments: [],
    created_at: demoTimestamp(7),
  },
  {
    id: 'demo-msg-4',
    conversation_id: DEMO_ROOM.id,
    sender_id: 'demo-player-1',
    sender_name: 'emilybrownfi7546',
    sender_role: 'player',
    body: 'Juwa',
    attachments: [],
    created_at: demoTimestamp(6),
  },
  {
    id: 'demo-msg-5',
    conversation_id: DEMO_ROOM.id,
    sender_id: 'demo-player-1',
    sender_name: 'emilybrownfi7546',
    sender_role: 'player',
    body: 'Give me you jw username',
    attachments: [],
    created_at: demoTimestamp(1),
  },
];

const readDemoMessages = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(LOCAL_DEMO_CHAT_KEY) || '[]');
    if (Array.isArray(stored) && stored.length) return stored;
  } catch {}
  const seeded = defaultDemoMessages();
  try { localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(seeded)); } catch {}
  return seeded;
};

const demoRoomFromMessages = () => {
  const items = readDemoMessages();
  const latest = items[items.length - 1];
  return {
    ...DEMO_ROOM,
    staff_unread: latest?.sender_role === 'player' ? 1 : 0,
    last_message: latest?.body || (latest?.attachments?.length ? 'Photo' : DEMO_ROOM.last_message),
    last_sender_role: latest?.sender_role || DEMO_ROOM.last_sender_role,
    last_sender_name: latest?.sender_name || DEMO_ROOM.last_sender_name,
    last_message_at: latest?.created_at || DEMO_ROOM.last_message_at,
    updated_at: latest?.created_at || DEMO_ROOM.updated_at,
  };
};

const syncDemoMessagesFromBridge = async () => {
  try {
    const res = await fetch(LOCAL_DEMO_BRIDGE_URL, { cache: 'no-store' });
    if (!res.ok) return readDemoMessages();
    const data = await res.json();
    if (Array.isArray(data.messages)) {
      localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(data.messages));
      return data.messages;
    }
  } catch {}
  return readDemoMessages();
};

const writeDemoMessages = (updater) => {
  const current = readDemoMessages();
  const next = typeof updater === 'function' ? updater(current) : updater;
  try { localStorage.setItem(LOCAL_DEMO_CHAT_KEY, JSON.stringify(next)); } catch {}
  try {
    fetch(LOCAL_DEMO_BRIDGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(() => {});
  } catch {}
  window.dispatchEvent(new Event(LOCAL_DEMO_CHAT_EVENT));
  try { new BroadcastChannel(LOCAL_DEMO_CHAT_EVENT).postMessage({ type: 'updated' }); } catch {}
  return next;
};

const demoAiDraftFromMessages = (items = readDemoMessages()) => {
  const latestPlayer = [...items].reverse().find(message => message.sender_role === 'player' && String(message.body || '').trim());
  const text = String(latestPlayer?.body || '').trim().toLowerCase();
  let draft = 'Sure, I am checking it now. Please send the details here and I will guide you step by step.';
  if (text.includes('load') || text.includes('deposit')) {
    draft = 'Sure, I can help you load. Please send your username and payment screenshot here so I can check it quickly.';
  } else if (text.includes('username') || text.includes('jw') || text.includes('juwa')) {
    draft = 'Sure, please send your Juwa username here. I will check it and guide you from there.';
  } else if (text.includes('hello') || text.includes('hi')) {
    draft = 'Hello, how can I help you today?';
  }
  return {
    ...DEMO_DRAFT,
    id: `demo-ai-draft-${latestPlayer?.id || 'default'}`,
    draft_text: draft,
  };
};

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) {
    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${window.location.origin}/api/chat/media${parsed.pathname.slice('/uploads'.length)}${parsed.search || ''}`;
      }
      if (parsed.pathname.startsWith('/api/chat/media/')) {
        return `${window.location.origin}${parsed.pathname}${parsed.search || ''}`;
      }
    } catch {
      return url;
    }
    return url;
  }
  if (url.startsWith('/api/chat/media/')) return url;
  if (url.startsWith('/uploads/')) return `/api/chat/media${url.slice('/uploads'.length)}`;
  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const parseChatDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const raw = String(value);
  const normalized = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const localDateKey = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return [date.getFullYear(), date.getMonth(), date.getDate()].join('-');
};

const dateSeparatorLabel = (value, previousValue) => {
  const currentKey = localDateKey(value);
  if (!currentKey || currentKey === localDateKey(previousValue)) return '';
  const date = parseChatDate(value);
  const now = new Date();
  const todayKey = localDateKey(now);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (currentKey === todayKey) return 'Today';
  if (currentKey === localDateKey(yesterday)) return 'Yesterday';
  return new Intl.DateTimeFormat([], {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
};

const roomTime = (room) => parseChatDate(room.last_message_at || room.updated_at || room.created_at)?.getTime() || 0;
const chatPlayerName = (room = {}) => room.chat_display_name || room.player_name || room.player_username || room.player_email || 'Anonymous';
const realPlayerName = (room = {}) => room.player_name || room.player_username || 'Player';
const playerEmail = (room = {}) => room.player_email || '';
const playerUsername = (room = {}) => String(room.player_username || room.username || '').trim();
const playerUsernameLabel = (room = {}) => {
  const username = playerUsername(room);
  if (!username) return '';
  return username.startsWith('@') ? username : `@${username}`;
};
const lastMessageSenderLabel = (room = {}) => {
  if (!room.last_sender_role) return '';
  if (room.last_sender_role === 'system') return '';
  return room.last_sender_role === 'player' ? 'Player' : 'You';
};
const isNewlyJoinedRoom = (room = {}) => room.newly_joined === true;
const newlyJoinedPreview = (room = {}) => (
  room.newly_joined_notice
  || `${room.player_name || room.player_username || room.player_email || 'Player'} joined ${room.tenant_name || 'this team'}`
);
const AGENT_SUBJECT_PREFIX = 'Texted to agent ';
const SLOTS_SOURCE_TENANT_ID = '661abea4-94a9-4a1e-acc2-b9dd3bb21268';
const SLOTS_CENTRAL_TENANT_ID = `slots-oceans:${SLOTS_SOURCE_TENANT_ID}`;
const sourceTenantIdForChat = (tenantId = '') => (
  tenantId === SLOTS_CENTRAL_TENANT_ID ? SLOTS_SOURCE_TENANT_ID : tenantId
);
const centralTenantIdForChat = (tenantId = '') => (
  tenantId === SLOTS_SOURCE_TENANT_ID ? SLOTS_CENTRAL_TENANT_ID : tenantId
);
const sameChatTenant = (left, right) => (
  centralTenantIdForChat(left) === centralTenantIdForChat(right)
);

const agentNameFromSubject = (subject = '') => {
  if (typeof subject !== 'string' || !subject.startsWith(AGENT_SUBJECT_PREFIX)) return '';
  return subject.slice(AGENT_SUBJECT_PREFIX.length).trim();
};

const agentNameForRoom = (room) => (
  room?.last_target_agent_name
  || room?.preferred_agent_name
  || agentNameFromSubject(room?.subject)
  || ''
);

const agentIdForRoom = (room) => room?.last_target_agent_id || room?.preferred_agent_id || '';
const agentTitleForRoom = (room) => room?.last_target_agent_title || room?.preferred_agent_title || '';
const agentAvatarForRoom = (room) => room?.last_target_agent_avatar_url || room?.preferred_agent_avatar_url || '';
const agentBioForRoom = (room) => room?.last_target_agent_bio || room?.preferred_agent_bio || '';
const normalizeAgentName = (value = '') => String(value || '').trim().toLowerCase();
const makeAgentKey = (id = '', name = '') => {
  const cleanName = normalizeAgentName(name);
  if (cleanName) return `name:${cleanName}`;
  const cleanId = String(id || '').trim();
  return cleanId ? `id:${cleanId}` : '';
};

const parseAgentTagFromBody = (body = '') => {
  if (typeof body !== 'string' || !body.startsWith(AGENT_SUBJECT_PREFIX)) {
    return { agentName: '', body: body || '' };
  }
  const [firstLine, ...rest] = body.split('\n');
  const agentName = firstLine.slice(AGENT_SUBJECT_PREFIX.length).trim();
  const cleanBody = rest.join('\n').replace(/^\n/, '');
  return { agentName, body: cleanBody };
};

const messagePreviewBody = (msg = {}) => (
  parseAgentTagFromBody(msg.body || '').body
  || (msg.attachments?.length ? 'Photo' : '')
);

const replyPreviewForMessage = (msg = {}) => ({
  id: msg.id,
  sender_id: msg.sender_id,
  sender_name: msg.sender_name || (msg.sender_role === 'player' ? 'Player' : 'Support'),
  sender_role: msg.sender_role,
  body: messagePreviewBody(msg).slice(0, 180),
  attachment_count: msg.attachments?.length || 0,
});

const cleanLinkHref = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '#';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const isInteractiveMessageTarget = (target) => Boolean(
  target?.closest?.('a[data-chat-link],button,input,textarea,[data-reaction-tray],[data-message-controls],[data-message-actions]')
);

const openChatLink = (event, href) => {
  event.stopPropagation();
  event.preventDefault();
  const opened = window.open(href, '_blank', 'noopener,noreferrer');
  if (!opened) window.location.assign(href);
};

const renderLinkedMessageText = (text, isAdmin) => {
  const parts = [];
  const source = String(text || '');
  let lastIndex = 0;
  source.replace(LINK_RE, (match, _url, offset) => {
    if (offset > lastIndex) parts.push(source.slice(lastIndex, offset));
    parts.push({ type: 'link', text: match, href: cleanLinkHref(match) });
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < source.length) parts.push(source.slice(lastIndex));
  return parts.map((part, index) => (
    typeof part === 'string' ? part : (
      <a
        key={`${part.href}-${index}`}
        data-chat-link
        href={part.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => openChatLink(e, part.href)}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="font-extrabold underline decoration-2 underline-offset-2"
        style={{ color: isAdmin ? '#FFFFFF' : '#0057D9' }}
      >
        {part.text}
      </a>
    )
  ));
};

const agentDetailsForMessage = (message = {}, fallbackRoom = {}) => {
  const parsedBody = parseAgentTagFromBody(message.body || '');
  return {
    id: message.target_agent_id || agentIdForRoom(fallbackRoom),
    name: message.target_agent_name || parsedBody.agentName || agentNameForRoom(fallbackRoom),
    title: message.target_agent_title || agentTitleForRoom(fallbackRoom),
    avatar_url: message.target_agent_avatar_url || agentAvatarForRoom(fallbackRoom),
    bio: message.target_agent_bio || agentBioForRoom(fallbackRoom),
  };
};

const AGENT_ACCENTS = [
  { bg: 'rgba(212,175,55,0.15)', border: 'rgba(212,175,55,0.42)', color: '#FDE68A' },
  { bg: 'rgba(56,189,248,0.14)', border: 'rgba(56,189,248,0.40)', color: '#7DD3FC' },
  { bg: 'rgba(244,114,182,0.14)', border: 'rgba(244,114,182,0.40)', color: '#F9A8D4' },
  { bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.42)', color: '#C4B5FD' },
  { bg: 'rgba(45,212,191,0.14)', border: 'rgba(45,212,191,0.40)', color: '#5EEAD4' },
];

const agentAccentForName = (name = '') => {
  const clean = normalizeAgentName(name);
  if (!clean) return AGENT_ACCENTS[0];
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) hash = (hash + clean.charCodeAt(i) * (i + 1)) % 997;
  return AGENT_ACCENTS[hash % AGENT_ACCENTS.length];
};

const sortRooms = (items) => [...items].sort((a, b) => {
  const timeDiff = roomTime(b) - roomTime(a);
  if (timeDiff) return timeDiff;
  return (b.staff_unread || 0) - (a.staff_unread || 0);
});

const roomSnapshotKey = (room = {}) => [
  room.id,
  room.updated_at,
  room.last_message_at,
  room.last_message,
  room.last_sender_role,
  room.last_sender_name,
  room.staff_unread,
  room.player_unread,
  room.status,
  room.player_online ? 'online' : 'offline',
  room.chat_display_name,
  room.player_username,
  room.assigned_staff_name,
  room.last_target_agent_name,
  room.preferred_agent_name,
  room.newly_joined ? 'new' : 'started',
  room.newly_joined_notice,
].map(value => value ?? '').join('|');

const sameRoomList = (current = [], next = []) => (
  current.length === next.length
  && current.every((room, index) => roomSnapshotKey(room) === roomSnapshotKey(next[index]))
);

const mergeRoomLists = (current = [], incoming = []) => {
  const byId = new Map();
  current.forEach(room => {
    if (room?.id) byId.set(room.id, room);
  });
  incoming.forEach(room => {
    if (!room?.id) return;
    byId.set(room.id, { ...(byId.get(room.id) || {}), ...room });
  });
  return sortRooms(Array.from(byId.values()));
};

const liveChatSocketUrl = (path) => {
  if (liveChatWsBaseUrl) return `${liveChatWsBaseUrl}${path}`;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
};

const mergeChatMessage = (items = [], incoming, limit = 100) => {
  if (!incoming?.id) return items;
  const clientId = incoming.client_message_id || '';
  let merged = false;
  const next = items.map(item => {
    const sameServerMessage = item.id === incoming.id;
    const samePendingMessage = clientId && item.client_message_id === clientId;
    if (sameServerMessage || samePendingMessage) {
      merged = true;
      return incoming;
    }
    return item;
  });
  if (!merged) next.push(incoming);

  const seen = new Set();
  const deduped = [];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    const item = next[i];
    const key = item.id && !String(item.id).startsWith('temp_')
      ? `id:${item.id}`
      : item.client_message_id
        ? `client:${item.client_message_id}`
        : `temp:${item.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.unshift(item);
  }
  return deduped.slice(-limit);
};

const mergeChatMessageList = (olderItems = [], currentItems = [], limit = MESSAGE_MEMORY_LIMIT) => {
  const seen = new Set();
  const merged = [];
  [...olderItems, ...currentItems].forEach((item) => {
    const key = item?.id || item?.client_message_id;
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });
  return merged.slice(-limit);
};

export default function ChatSupportPage({ embedded = false, agentPortal = false, onUnreadCountChange }) {
  const { user } = useAuth();
  const { theme } = useTenantTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const isChatRoute = embedded || /\/chat\/?$/.test(pathname);
  const chatRouteVisibleRef = useRef(isChatRoute);
  chatRouteVisibleRef.current = isChatRoute;
  const isMasterAdmin = Boolean(user?.is_master_admin);
  const isAgentPortal = agentPortal || user?.role === 'referral_agent';
  const visiblePlayerName = useCallback((room = {}) => (
    isAgentPortal
      ? (room.chat_display_name || room.player_name || room.player_id || 'Customer')
      : chatPlayerName(room)
  ), [isAgentPortal]);
  const [adminChatMode, setAdminChatMode] = useState('customer');
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [archivedHistory, setArchivedHistory] = useState({ key: '', messages: [], loading: false, error: '' });
  const [archiveRetry, setArchiveRetry] = useState(0);
  const [loadedMessageContext, setLoadedMessageContext] = useState(null);
  const composer = useChatComposer();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [search, setSearch] = useState('');
  const [openingSearchPlayerId, setOpeningSearchPlayerId] = useState('');
  const openingSearchPlayerRef = useRef(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [roomPageInfo, setRoomPageInfo] = useState({ total: null, hasMore: false, nextCursor: null });
  const [loadingOlderRooms, setLoadingOlderRooms] = useState(false);
  const [refreshingMessages, setRefreshingMessages] = useState(false);
  const [refreshingRooms, setRefreshingRooms] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [activeReactionId, setActiveReactionId] = useState(null);
  const [expandedReactionId, setExpandedReactionId] = useState(null);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [messageMenuMode, setMessageMenuMode] = useState('controls');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const editInFlightRef = useRef(false);
  const editVersionRef = useRef(0);
  const chatActorId = useChatActor(user, isLocalDemoChat(), activeMessageMenuId);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [visibleTimeId, setVisibleTimeId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState(false);
  const [roomNameDraft, setRoomNameDraft] = useState('');
  const [roomLabelDrafts, setRoomLabelDrafts] = useState([]);
  const [roomLabelInput, setRoomLabelInput] = useState('');
  const [typingByRoom, setTypingByRoom] = useState({});
  const [aiDraft, setAiDraft] = useState(null);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [isCompactScreen, setIsCompactScreen] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  ));
  const sendingRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesPaneRef = useRef(null);
  const forceNextMessageScrollRef = useRef(true);
  const userScrollingMessagesRef = useRef(false);
  const messageScrollTimerRef = useRef(null);
  const ignoreProgrammaticMessageScrollRef = useRef(false);
  const messageScrollPinTimersRef = useRef([]);
  const wsRef = useRef(null);
  const selectedRoomRef = useRef(null);
  const roomsRef = useRef([]);
  const lastMessageContextRef = useRef({ roomId: '', agentFilter: '' });
  const [favorites, setFavorites] = useState([]);
  const [filterMode, setFilterMode] = useState('all');
  const [agentConversationMode, setAgentConversationMode] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [configuredAgents, setConfiguredAgents] = useState([]);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(() => sourceTenantIdForChat(searchParams.get('tenant_id') || ''));
  const activeChatTenantId = selectedTenantId || (isMasterAdmin ? '' : (user?.tenant_id || ''));
  const directorySearch = useChatPlayerSearch({
    search,
    tenantId: activeChatTenantId,
    agentPortal: isAgentPortal,
    enabled: isChatRoute && !isLocalDemoChat() && filterMode === 'all'
      && (isAgentPortal || agentFilter === 'all')
      && (!isMasterAdmin || agentConversationMode === 'all'),
  });
  const searchWorkspaceRef = useRef('');
  searchWorkspaceRef.current = JSON.stringify([activeChatTenantId, isAgentPortal, agentConversationMode, filterMode, agentFilter, isChatRoute]);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [playerDetails, setPlayerDetails] = useState(null);
  const [playerDetailsPlayerId, setPlayerDetailsPlayerId] = useState('');
  const [bulkRecallOpen, setBulkRecallOpen] = useState(false);
  const [bulkRecallMode, setBulkRecallMode] = useState('selected');
  const [bulkRecallMessage, setBulkRecallMessage] = useState('');
  const [bulkRecallSearch, setBulkRecallSearch] = useState('');
  const [bulkRecallResults, setBulkRecallResults] = useState([]);
  const [bulkRecallSelected, setBulkRecallSelected] = useState([]);
  const [bulkRecallAgentKey, setBulkRecallAgentKey] = useState('');
  const [bulkRecallSearching, setBulkRecallSearching] = useState(false);
  const [bulkRecallSending, setBulkRecallSending] = useState(false);
  const [bulkRecallResult, setBulkRecallResult] = useState(null);
  const { inputRef } = composer;
  const photoRef = useRef(null);
  const reconnectAllowedRef = useRef(false);
  const reconnectTimer = useRef(null);
  const reactionTimerRef = useRef(null);
  const messageSwipeRef = useRef(null);
  const swipeActivatedRef = useRef(false);
  const previewHistoryRef = useRef(false);
  const mobileChatOpenRef = useRef(false);
  const mobileChatHistoryRef = useRef(false);
  const readAckTimersRef = useRef(new Map());
  const readAckInFlightRef = useRef(new Set());
  const readAckPendingRef = useRef(new Set());
  const renderedReadRef = useRef(null);
  const completedReadAckRef = useRef(new Map());
  const readAckFailuresRef = useRef(new Map());
  const unreadStateRef = useRef(createStaffUnreadState());
  const messageLoadSeqRef = useRef(0);
  const touchStartYRef = useRef(null);
  const roomsPaneRef = useRef(null);
  const roomsTouchStartYRef = useRef(null);
  const agentFilterRef = useRef('all');

  const agentOptionsRef = useRef([]);
  const roomFetchInFlightRef = useRef(false);
  const roomFetchPendingRef = useRef(false);
  const roomFetchPendingOptionsRef = useRef(null);
  const roomFetchSeqRef = useRef(0);
  const searchRef = useRef('');
  const roomFetchRetryTimerRef = useRef(null);
  const roomFetchRecoveryTimerRef = useRef(null);
  const roomLoadFailureCountRef = useRef(0);
  const aiDraftFetchRef = useRef({ roomId: '', at: 0 });
  const typingTimerRef = useRef(null);
  const typingPulseTimerRef = useRef(null);
  const typingStopTimerRef = useRef(null);
  const selectedTenantIdRef = useRef('');
  const handledDirectOpenRef = useRef('');

  // Keep selectedRoomRef in sync
  useEffect(() => { selectedRoomRef.current = selectedRoom; }, [selectedRoom]);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);
  useEffect(() => { searchRef.current = search; }, [search]);
  useEffect(() => { mobileChatOpenRef.current = mobileChatOpen; }, [mobileChatOpen]);
  useEffect(() => {
    if (!isAgentPortal || !selectedRoom?.player_id) {
      setPlayerDetails(null);
      setPlayerDetailsPlayerId('');
      return undefined;
    }

    let cancelled = false;
    const playerId = selectedRoom.player_id;
    const sourceIdentity = sourceChatPlayerIdentity(playerId, selectedRoom.tenant_id || user?.tenant_id);
    setPlayerDetails(null);
    setPlayerDetailsPlayerId(playerId);
    setDetailsLoading(true);
    api.get('/api/agent-earn/me/players/' + encodeURIComponent(sourceIdentity.playerId) + '/preview')
      .then((res) => {
        if (!cancelled) setPlayerDetails(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setPlayerDetails(null);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAgentPortal, selectedRoom?.player_id, selectedRoom?.tenant_id, user?.tenant_id]);
  useEffect(() => {
    setRoomNameDraft(visiblePlayerName(selectedRoom || {}));
    setRoomLabelDrafts(roomLabels(selectedRoom || {}));
    setRoomLabelInput('');
    setEditingRoomName(false);
    setAiDraft(null);
  }, [selectedRoom?.id, selectedRoom?.chat_display_name, selectedRoom?.player_name, selectedRoom?.labels, visiblePlayerName]);

  const agentPortalAgent = useMemo(() => {
    if (!isAgentPortal) return null;
    const profile = user?.agent_chat_profile || {};
    const id = String(user?.id || '').trim();
    const profileName = String(profile.display_name || '').trim();
    const name = (profileName && profileName.toLowerCase() !== 'agent'
      ? profileName
      : String(user?.full_name || user?.username || 'Agent').trim()) || 'Agent';
    if (!id && !name) return null;
    return {
      key: makeAgentKey(id, name),
      id,
      name,
      title: profile.title || 'Live Agent',
      avatar_url: profile.avatar_url || user?.avatar_url || '',
      bio: profile.bio || '',
    };
  }, [isAgentPortal, user]);

  const agentOptions = useMemo(() => {
    if (isAgentPortal) return agentPortalAgent ? [agentPortalAgent] : [];
    return [
      { key: 'all', id: '', name: 'All agents', title: '', avatar_url: '' },
      ...configuredChatAgents(configuredAgents, activeChatTenantId, centralChatTicketsEnabled),
    ];
  }, [configuredAgents, activeChatTenantId, isAgentPortal, agentPortalAgent]);

  useEffect(() => { agentOptionsRef.current = agentOptions; }, [agentOptions]);
  useEffect(() => { agentFilterRef.current = agentFilter; }, [agentFilter]);
  useEffect(() => { selectedTenantIdRef.current = selectedTenantId; }, [selectedTenantId]);
  useEffect(() => {
    if (isAgentPortal) {
      if (agentPortalAgent?.key && agentFilter !== agentPortalAgent.key) {
        setAgentFilter(agentPortalAgent.key);
      }
      return;
    }
    if (agentFilter !== 'all' && !agentOptions.some(option => option.key === agentFilter)) {
      setAgentFilter('all');
    }
  }, [agentFilter, agentOptions, isAgentPortal, agentPortalAgent?.key]);

  const getAgentOption = (key = agentFilterRef.current) => {
    if (isAgentPortal) return agentPortalAgent || agentOptionsRef.current[0] || null;
    return key === 'all' ? null : agentOptionsRef.current.find(option => option.key === key);
  };

  const bulkRecallAgentOptions = useMemo(() => (
    configuredChatAgents(configuredAgents, activeChatTenantId, centralChatTicketsEnabled)
  ), [configuredAgents, activeChatTenantId]);

  useEffect(() => {
    if (!bulkRecallOpen) return;
    setBulkRecallAgentKey(current => (
      bulkRecallAgentOptions.some(option => option.key === current)
        ? current
        : (bulkRecallAgentOptions[0]?.key || '')
    ));
  }, [bulkRecallOpen, bulkRecallAgentOptions]);

  const messageMatchesAgentFilter = (message, fallbackRoom = selectedRoomRef.current, filterKey = agentFilterRef.current) => {
    const selectedAgent = isAgentPortal ? getAgentOption() : getAgentOption(filterKey);
    if (!isAgentPortal && (!filterKey || filterKey === 'all')) return true;
    if (!selectedAgent) return !isAgentPortal;
    const details = agentDetailsForMessage(message, fallbackRoom || {});
    const idMatches = selectedAgent.id && details.id && String(selectedAgent.id) === String(details.id);
    const nameMatches = selectedAgent.name && normalizeAgentName(details.name) === normalizeAgentName(selectedAgent.name);
    return Boolean(idMatches || nameMatches);
  };

  const roomMatchesAgentFilter = (room, selectedAgent = getAgentOption()) => {
    if (!isAgentPortal) return true;
    if (!selectedAgent) return false;
    const roomAgentId = agentIdForRoom(room);
    const roomAgentName = agentNameForRoom(room);
    const idMatches = selectedAgent.id && roomAgentId && String(selectedAgent.id) === String(roomAgentId);
    const nameMatches = selectedAgent.name && normalizeAgentName(roomAgentName) === normalizeAgentName(selectedAgent.name);
    return Boolean(idMatches || nameMatches);
  };

  // ---- WebSocket Connection ----
  const connectWS = useCallback(async () => {
    const token = getStoredSessionToken({ portal: isAgentPortal ? 'agent' : 'admin' });
    if (!reconnectAllowedRef.current || !token) return;
    if (isLocalDemoChat()) {
      setWsConnected(true);
      return;
    }

    let socketTicket = null;
    try {
      socketTicket = await createLiveChatSocketTicket({ kind: 'staff' });
    } catch {
      if (reconnectAllowedRef.current) {
        reconnectTimer.current = setTimeout(() => void connectWS(), 2500);
      }
      return;
    }

    if (!reconnectAllowedRef.current || getStoredSessionToken({ portal: isAgentPortal ? 'agent' : 'admin' }) !== token) return;
    const socketAuth = centralChatTicketsEnabled
      ? `ticket=${encodeURIComponent(socketTicket)}`
      : `token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(liveChatSocketUrl(`/ws/staff?${socketAuth}`));

    ws.onopen = () => {
      setWsConnected(true);
      // Send ping every 25s to keep alive
      ws._pingInterval = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'conversation.updated') {
          // Update rooms list instantly
          const roomId = data.conversation_id || data.message?.conversation_id || data.conversation?.id;
          if (data.conversation && !data.message) {
            setRooms(prev => sortRooms(prev.map(room => (
              room.id === data.conversation.id ? { ...room, ...data.conversation } : room
            ))));
            setSelectedRoom(prev => (
              prev?.id === data.conversation.id ? { ...prev, ...data.conversation } : prev
            ));
            return;
          }
          const isPlayerMessage = data.message?.sender_role === 'player';
          const eventTenantId = data.message?.tenant_id || data.conversation?.tenant_id;
          if (isMasterAdmin && selectedTenantIdRef.current && eventTenantId && !sameChatTenant(eventTenantId, selectedTenantIdRef.current)) {
            return;
          }
          const isSelectedRoom = selectedRoomRef.current?.id === roomId;
          const newUnreadMessage = unreadStateRef.current.acceptMessage(data.message);
          const knownRoom = roomsRef.current.find(room => room.id === roomId);
          const unreadCount = unreadStateRef.current.count(roomId, knownRoom?.staff_unread)
            + (newUnreadMessage ? 1 : 0);
          if (knownRoom && newUnreadMessage) unreadStateRef.current.update(roomId, unreadCount);
          setRooms(prev => {
            const idx = prev.findIndex(r => r.id === roomId);
            if (idx >= 0) {
              const updated = [...prev];
              const updatedRoom = {
                ...updated[idx],
                last_message: data.message?.body || 'Photo',
                last_sender_role: data.message?.sender_role || updated[idx].last_sender_role,
                last_sender_name: data.message?.sender_name || updated[idx].last_sender_name,
                last_message_at: data.message?.created_at,
                updated_at: data.message?.created_at,
                last_target_agent_id: data.message?.target_agent_id || updated[idx].last_target_agent_id,
                last_target_agent_name: data.message?.target_agent_name || updated[idx].last_target_agent_name,
                last_target_agent_title: data.message?.target_agent_title || updated[idx].last_target_agent_title,
                last_target_agent_avatar_url: data.message?.target_agent_avatar_url || updated[idx].last_target_agent_avatar_url,
                last_target_agent_bio: data.message?.target_agent_bio || updated[idx].last_target_agent_bio,
                preferred_agent_id: data.message?.target_agent_id || updated[idx].preferred_agent_id,
                preferred_agent_name: data.message?.target_agent_name || updated[idx].preferred_agent_name,
                preferred_agent_title: data.message?.target_agent_title || updated[idx].preferred_agent_title,
                preferred_agent_avatar_url: data.message?.target_agent_avatar_url || updated[idx].preferred_agent_avatar_url,
                preferred_agent_bio: data.message?.target_agent_bio || updated[idx].preferred_agent_bio,
                newly_joined: false,
                conversation_started_at: data.message?.created_at || updated[idx].conversation_started_at,
                staff_unread: unreadStateRef.current.count(roomId, updated[idx].staff_unread),
              };
              updated.splice(idx, 1);
              return [updatedRoom, ...updated];
            }
            fetchRooms();
            return prev;
          });

          // If this room is selected, add message
          if (isSelectedRoom && data.message && messageMatchesAgentFilter(data.message, selectedRoomRef.current)) {
            setMessages(prev => mergeChatMessage(prev, data.message, MESSAGE_MEMORY_LIMIT));
            setSelectedRoom(prev => (
              prev?.id === data.message.conversation_id
                ? { ...prev, newly_joined: false, conversation_started_at: data.message.created_at || prev.conversation_started_at }
                : prev
            ));
          }
          if (newUnreadMessage && data.message) {
            notifyIncomingPlayerMessage(data.message);
            if (isSelectedRoom) {
              setTimeout(() => fetchAiDraft(roomId, { quiet: true }), 1400);
            }
          }
        }

        if (data.type === 'message.reaction' && data.message) {
          setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
        }

        if (data.type === 'conversation.read' && data.conversation_id) {
          if (isMasterAdmin && selectedTenantIdRef.current && data.tenant_id && !sameChatTenant(data.tenant_id, selectedTenantIdRef.current)) {
            return;
          }
          const remainingUnread = confirmedStaffUnread(data);
          unreadStateRef.current.update(data.conversation_id, remainingUnread);
          setRooms(prev => prev.map(room => (
            room.id === data.conversation_id ? { ...room, staff_unread: remainingUnread } : room
          )));
          setSelectedRoom(prev => (
            prev?.id === data.conversation_id ? { ...prev, staff_unread: remainingUnread } : prev
          ));
        }

        if ((data.type === 'message.updated' || data.type === 'message.deleted') && data.message) {
          setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
          setRooms(prev => prev.map(room => (
            room.id === data.message.conversation_id
              ? { ...room, last_message: data.message.deleted ? '' : (data.message.body || (data.message.attachments?.length ? 'Photo' : room.last_message)) }
              : room
          )));
        }

        if (data.type === 'conversation.important' && data.player_id) {
          const important = Boolean(data.is_important);
          setFavorites(prev => (
            important
              ? (prev.includes(data.player_id) ? prev : [...prev, data.player_id])
              : prev.filter(id => id !== data.player_id)
          ));
          setRooms(prev => prev.map(room => (
            room.player_id === data.player_id ? { ...room, is_important: important } : room
          )));
          setSelectedRoom(prev => (
            prev?.player_id === data.player_id ? { ...prev, is_important: important } : prev
          ));
        }

        if (data.type === 'conversation.presence') {
          setRooms(prev => prev.map(room => (
            room.id === data.conversation_id ? { ...room, player_online: Boolean(data.player_online) } : room
          )));
          setSelectedRoom(prev => (
            prev?.id === data.conversation_id ? { ...prev, player_online: Boolean(data.player_online) } : prev
          ));
        }

        if (data.type === 'typing' && data.sender_role === 'player') {
          setTypingByRoom(prev => ({ ...prev, [data.conversation_id]: Boolean(data.is_typing) }));
          if (data.is_typing) {
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = setTimeout(() => {
              setTypingByRoom(prev => ({ ...prev, [data.conversation_id]: false }));
            }, 3500);
          }
        }

        if (data.type === 'conversation.created') {
          if (!isMasterAdmin || !selectedTenantIdRef.current || sameChatTenant(data.conversation?.tenant_id, selectedTenantIdRef.current)) {
            fetchRooms();
          }
        }
      } catch {}
    };

    ws.onclose = () => {
      setWsConnected(false);
      clearInterval(ws._pingInterval);
      // Auto reconnect in 2s
      if (reconnectAllowedRef.current) {
        reconnectTimer.current = setTimeout(() => connectWS(), 2000);
      }
    };

    ws.onerror = () => { ws.close(); };

    wsRef.current = ws;
  }, [isAgentPortal, isMasterAdmin]);

  const ensureStaffNotifications = () => {
    try {
      const bridge = window.AndroidStaffChat;
      if (bridge?.requestNotifications) {
        bridge.requestNotifications();
      }
    } catch {}
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {}
  };

  useEffect(() => {
    reconnectAllowedRef.current = true;
    connectWS();
    return () => {
      reconnectAllowedRef.current = false;
      clearTimeout(reconnectTimer.current);
      clearTimeout(typingTimerRef.current);
      clearInterval(typingPulseTimerRef.current);
      clearTimeout(typingStopTimerRef.current);
      clearTimeout(messageScrollTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWS]);

  // ---- Initial Data Load ----
  useEffect(() => {
    fetchRooms({ reset: true });
    fetchFavorites();
    fetchConfiguredAgents();
    fetchTenantOptions();
    ensureStaffNotifications();
  }, []);

  useEffect(() => {
    if (!isMasterAdmin) return;
    setSelectedRoom(null);
    setMessages([]);
    setMobileChatOpen(false);
    fetchRooms({ reset: true });
    fetchFavorites();
    fetchConfiguredAgents();
  }, [selectedTenantId, isMasterAdmin]);

  useEffect(() => {
    if (isMasterAdmin && selectedTenantId) fetchConfiguredAgents();
  }, [tenantOptions, selectedTenantId, isMasterAdmin]);

  useEffect(() => {
    if (!isLocalDemoChat()) return undefined;
    const syncDemoChat = () => {
      const applyMessages = (items) => {
        const latest = items[items.length - 1];
        const nextRoom = {
          ...DEMO_ROOM,
          staff_unread: latest?.sender_role === 'player' ? 1 : 0,
          last_message: latest?.body || (latest?.attachments?.length ? 'Photo' : DEMO_ROOM.last_message),
          last_sender_role: latest?.sender_role || DEMO_ROOM.last_sender_role,
          last_sender_name: latest?.sender_name || DEMO_ROOM.last_sender_name,
          last_message_at: latest?.created_at || DEMO_ROOM.last_message_at,
          updated_at: latest?.created_at || DEMO_ROOM.updated_at,
        };
        setRooms(sortRooms([nextRoom]));
        setSelectedRoom(prev => (prev?.id === nextRoom.id ? { ...prev, ...nextRoom } : prev));
        if (selectedRoomRef.current?.id === nextRoom.id) setMessages(items);
        setAiDraft(demoAiDraftFromMessages(items));
      };
      applyMessages(readDemoMessages());
      syncDemoMessagesFromBridge().then(applyMessages);
    };
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(LOCAL_DEMO_CHAT_EVENT) : null;
    const onStorage = (event) => {
      if (!event || event.key === LOCAL_DEMO_CHAT_KEY) syncDemoChat();
    };
    syncDemoChat();
    window.addEventListener(LOCAL_DEMO_CHAT_EVENT, syncDemoChat);
    window.addEventListener('storage', onStorage);
    if (channel) channel.onmessage = syncDemoChat;
    const interval = setInterval(syncDemoChat, 500);
    return () => {
      window.removeEventListener(LOCAL_DEMO_CHAT_EVENT, syncDemoChat);
      window.removeEventListener('storage', onStorage);
      if (channel) channel.close();
      clearInterval(interval);
    };
  }, []);

  // ---- Refresh rooms periodically as fallback ----
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      fetchRooms();
    }, wsConnected ? 30000 : 6000);
    return () => clearInterval(interval);
  }, [wsConnected, selectedTenantId, isMasterAdmin]);

  useEffect(() => {
    let lastRegisteredToken = '';
    const registerToken = async (token) => {
      const cleanToken = String(token || '').trim();
      if (!cleanToken || cleanToken === lastRegisteredToken) return;
      try {
        await liveChatApi.post('/api/chat/staff/devices', {
          token: cleanToken,
          platform: 'android',
          app: 'staff-chat',
          tenant_id: selectedTenantId || undefined,
        });
        lastRegisteredToken = cleanToken;
      } catch (error) {
        console.warn('Could not register staff push token', error);
      }
    };
    const pullTokenFromBridge = () => {
      try {
        const bridge = window.AndroidStaffChat;
        if (!bridge) return;
        bridge.requestNotifications?.();
        bridge.refreshFcmToken?.();
        const token = bridge.getFcmToken?.();
        if (token) registerToken(token);
      } catch (error) {
        console.warn('Could not request staff push token', error);
      }
    };
    const onToken = (event) => registerToken(event?.detail?.token);
    const onTokenError = (event) => console.warn('Staff FCM token error', event?.detail?.error);
    window.addEventListener('staff-fcm-token', onToken);
    window.addEventListener('staff-fcm-token-error', onTokenError);
    const onVisible = () => {
      if (document.visibilityState === 'visible') pullTokenFromBridge();
    };
    document.addEventListener('visibilitychange', onVisible);
    pullTokenFromBridge();
    const retryTimers = [800, 2500, 6000].map(delay => setTimeout(pullTokenFromBridge, delay));
    const interval = setInterval(pullTokenFromBridge, 45000);
    return () => {
      window.removeEventListener('staff-fcm-token', onToken);
      window.removeEventListener('staff-fcm-token-error', onTokenError);
      document.removeEventListener('visibilitychange', onVisible);
      retryTimers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, [selectedTenantId]);

  // ---- Load messages when room selected ----
  useEffect(() => {
    if (selectedRoom) {
      // On phones the selected room remains in memory after returning to the
      // room list. Do not let a filter/resize refresh mark messages read while
      // the conversation pane is closed.
      if (isCompactScreen && !mobileChatOpen) return;
      const context = { roomId: selectedRoom.id, agentFilter };
      const switchedRoom = lastMessageContextRef.current.roomId !== context.roomId
        || lastMessageContextRef.current.agentFilter !== context.agentFilter;
      lastMessageContextRef.current = context;
      if (switchedRoom) {
        renderedReadRef.current = null;
        setLoadedMessageContext(null);
        setMessages([]);
      }
      setHasOlderMessages(false);
      setLoadingOlderMessages(false);
      userScrollingMessagesRef.current = false;
      forceNextMessageScrollRef.current = true;
      fetchMessages(selectedRoom.id, { markRead: isConversationVisible(selectedRoom.id) });
    }
  }, [selectedRoom?.id, mobileChatOpen, agentFilter, isCompactScreen]);

  // Runs after React commits the message list, never on an unopened room or
  // on the previous agent's messages while a new filter is loading.
  useEffect(() => {
    const roomId = selectedRoom?.id;
    if (loadedMessageContext?.roomId !== roomId || loadedMessageContext?.agentFilter !== agentFilter) {
      renderedReadRef.current = null;
      return;
    }
    const payload = staffReadAcknowledgement(
      messages.filter(message => messageMatchesAgentFilter(message)), roomId, getAgentOption(),
    );
    renderedReadRef.current = payload ? { roomId, agentFilter, payload } : null;
    if (payload && isConversationVisible(roomId)) acknowledgeConversationRead(roomId);
  }, [messages, loadedMessageContext, selectedRoom?.id, agentFilter, mobileChatOpen, isChatRoute]);

  useEffect(() => {
    if (!selectedRoom?.id) return undefined;
    const interval = setInterval(() => {
      if (!isConversationVisible(selectedRoom.id)) return;
      if (wsConnected) return;
      if (userScrollingMessagesRef.current || !isMessagePanePinnedToBottom()) return;
      fetchMessages(selectedRoom.id, { quiet: true });
    }, wsConnected ? 6000 : 2500);
    return () => clearInterval(interval);
  }, [selectedRoom?.id, wsConnected, mobileChatOpen, agentFilter]);

  useEffect(() => {
    const acknowledgeVisibleRoom = () => {
      const roomId = selectedRoomRef.current?.id;
      if (roomId && isConversationVisible(roomId)) {
        acknowledgeConversationRead(roomId, { immediate: true });
      }
    };
    document.addEventListener('visibilitychange', acknowledgeVisibleRoom);
    return () => document.removeEventListener('visibilitychange', acknowledgeVisibleRoom);
  }, [selectedRoom?.id, mobileChatOpen]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    const room = selectedRoom;
    const key = room?.id && room?.player_id ? `${room.id}|${room.player_id}` : '';
    const source = sourceChatPlayerIdentity(room?.player_id, room?.tenant_id);
    if (!key || isAgentPortal || isLocalDemoChat()
      || source.tenantId !== '661abea4-94a9-4a1e-acc2-b9dd3bb21268') {
      setArchivedHistory({ key, messages: [], loading: false, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setArchivedHistory({ key, messages: [], loading: true, error: '' });
    api.get(`/api/chat-archive/players/${encodeURIComponent(source.playerId)}/messages`, { signal: controller.signal })
      .then(res => {
        if (!controller.signal.aborted) setArchivedHistory({
          key, messages: Array.isArray(res.data?.messages) ? res.data.messages : [],
          loading: false, error: '', hasMore: Boolean(res.data?.has_more),
        });
      })
      .catch(error => {
        if (!controller.signal.aborted) setArchivedHistory({
          key, messages: [], loading: false,
          error: error?.response?.status === 404 ? '' : 'Previous chat history could not load.',
        });
      });
    return () => controller.abort();
  }, [selectedRoom?.id, selectedRoom?.player_id, selectedRoom?.tenant_id, isAgentPortal, archiveRetry]);

  useEffect(() => {
    return () => {
      messageScrollPinTimersRef.current.forEach(clearTimeout);
      readAckTimersRef.current.forEach(clearTimeout);
      readAckTimersRef.current.clear();
      readAckInFlightRef.current.clear();
      readAckPendingRef.current.clear();
      readAckFailuresRef.current.clear();
      clearTimeout(roomFetchRetryTimerRef.current);
      clearTimeout(roomFetchRecoveryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const closeOnBack = () => {
      if (previewHistoryRef.current) {
        previewHistoryRef.current = false;
        setPhotoPreview(null);
        return;
      }
      if (mobileChatOpenRef.current) {
        mobileChatHistoryRef.current = false;
        mobileChatOpenRef.current = false;
        setMobileChatOpen(false);
      }
    };
    window.addEventListener('popstate', closeOnBack);
    return () => window.removeEventListener('popstate', closeOnBack);
  }, []);

  useEffect(() => {
    let viewportFrame = null;
    let lastHeight = '';
    let lastTop = '';
    const syncViewport = () => {
      viewportFrame = null;
      const compact = window.matchMedia?.('(max-width: 767px)').matches ?? false;
      setIsCompactScreen(compact);
      const viewport = window.visualViewport;
      const height = `${Math.max(1, Math.round(viewport?.height || window.innerHeight))}px`;
      const top = `${Math.max(0, Math.round(viewport?.offsetTop || 0))}px`;
      if (height !== lastHeight) {
        document.documentElement.style.setProperty('--staff-chat-viewport-height', height);
        lastHeight = height;
      }
      if (top !== lastTop) {
        document.documentElement.style.setProperty('--staff-chat-viewport-top', top);
        lastTop = top;
      }
    };
    const scheduleViewport = () => {
      if (viewportFrame === null) viewportFrame = requestAnimationFrame(syncViewport);
    };

    syncViewport();
    window.addEventListener('resize', scheduleViewport);
    window.addEventListener('orientationchange', scheduleViewport);
    window.addEventListener('pageshow', scheduleViewport);
    window.visualViewport?.addEventListener('resize', scheduleViewport);
    window.visualViewport?.addEventListener('scroll', scheduleViewport);
    return () => {
      if (viewportFrame !== null) cancelAnimationFrame(viewportFrame);
      window.removeEventListener('resize', scheduleViewport);
      window.removeEventListener('orientationchange', scheduleViewport);
      window.removeEventListener('pageshow', scheduleViewport);
      window.visualViewport?.removeEventListener('resize', scheduleViewport);
      window.visualViewport?.removeEventListener('scroll', scheduleViewport);
      document.documentElement.style.removeProperty('--staff-chat-viewport-height');
      document.documentElement.style.removeProperty('--staff-chat-viewport-top');
    };
  }, []);

  const fetchRooms = async (options = {}) => {
    if (roomFetchInFlightRef.current) {
      roomFetchPendingRef.current = true;
      roomFetchPendingOptionsRef.current = options.reset
        ? { reset: true }
        : options.append
          ? options
          : (roomFetchPendingOptionsRef.current || {});
      return;
    }
    roomFetchInFlightRef.current = true;
    const seq = ++roomFetchSeqRef.current;
    const append = Boolean(options.append);
    const reset = Boolean(options.reset);
    const requestTenantId = selectedTenantId;
    const requestSearch = search.trim();
    const unreadSnapshotVersion = unreadStateRef.current.version();
    let preserveLoadingForRecovery = false;
    try {
      if (isLocalDemoChat()) {
        const nextRoom = demoRoomFromMessages();
        const nextRooms = sortRooms([nextRoom]);
        setRooms(prev => (sameRoomList(prev, nextRooms) ? prev : nextRooms));
        setRoomPageInfo({ total: nextRooms.length, hasMore: false, nextCursor: null });
        if (!selectedRoomRef.current) setSelectedRoom(nextRoom);
        return;
      }
      // A master account gets its tenant selector a moment after boot.  Do
      // not briefly request the global roster in that gap: those rooms can
      // belong to a different tenant than the selected admin inbox.
      if (isMasterAdmin && !selectedTenantId) {
        setRooms([]);
        setRoomPageInfo({ total: 0, hasMore: false, nextCursor: null });
        return;
      }
      const params = new URLSearchParams({ status: 'all', limit: String(ROOM_FETCH_LIMIT), include_total: 'false' });
      if (append) {
        const cursor = options.cursor || roomPageInfo.nextCursor;
        if (cursor) params.set('cursor', cursor);
      }
      if (search.trim().length >= 2) params.set('search', search.trim());
      if (isMasterAdmin && selectedTenantId) params.set('tenant_id', centralTenantIdForChat(selectedTenantId));
      if (isMasterAdmin && agentConversationMode === 'agent_only') params.set('agent_only', 'true');
      if (isAgentPortal) {
        const selectedAgent = getAgentOption();
        if (selectedAgent?.id) params.set('agent_id', selectedAgent.id);
        if (selectedAgent?.name) params.set('agent_name', selectedAgent.name);
      }
      const res = await liveChatApi.get(`/api/chat/conversations?${params.toString()}`);
      if (seq !== roomFetchSeqRef.current) return;
      if (selectedTenantIdRef.current !== requestTenantId || searchRef.current.trim() !== requestSearch) return;
      const pageRooms = sortRooms((res.data.conversations || []).map(room => (
        unreadStateRef.current.snapshot(room, unreadSnapshotVersion)
      ))).filter((room) => (
        roomMatchesAgentFilter(room)
        && (!activeChatTenantId || sameChatTenant(room.tenant_id, activeChatTenantId))
      ));
      const nextRooms = append
        ? mergeRoomLists(roomsRef.current, pageRooms)
        : reset ? pageRooms : mergeRoomLists(roomsRef.current, pageRooms);
      const total = res.data.total !== null && res.data.total !== undefined && Number.isFinite(Number(res.data.total))
        ? Number(res.data.total) : null;
      const nextCursor = res.data.next_cursor || null;
      setRoomPageInfo(previous => (
        !append && !reset && roomsRef.current.length > ROOM_FETCH_LIMIT
          ? { ...previous, total: total ?? previous.total }
          : { total, hasMore: Boolean(res.data.has_more && nextCursor), nextCursor }
      ));
      setRooms(prev => (sameRoomList(prev, nextRooms) ? prev : nextRooms));
      clearTimeout(roomFetchRecoveryTimerRef.current);
      roomLoadFailureCountRef.current = 0;
      setLoadError('');
    } catch (error) {
      // A bad mobile connection used to leave the embedded portal on a blank
      // screen because its loading return had no visible fallback. Keep the
      // chat frame available and make the recovery action explicit instead.
      if (seq === roomFetchSeqRef.current) {
        const transient = isTransientChatLoadError(error);
        const failureCount = roomLoadFailureCountRef.current + 1;
        roomLoadFailureCountRef.current = failureCount;
        const retryDelay = CHAT_RECOVERY_DELAYS_MS[failureCount - 1];
        if (transient && retryDelay !== undefined) {
          clearTimeout(roomFetchRecoveryTimerRef.current);
          roomFetchRecoveryTimerRef.current = window.setTimeout(() => {
            fetchRooms({ reset: true });
          }, retryDelay);
          // During a short proxy restart, keep existing conversations visible.
          // On an initial load, retain the loader until automatic recovery has
          // had a fair chance instead of freezing a Cloudflare error on screen.
          preserveLoadingForRecovery = roomsRef.current.length === 0;
          setLoadError('');
          if (preserveLoadingForRecovery) setLoading(true);
        } else {
          setLoadError(chatLoadErrorMessage(error));
        }
      }
    } finally {
      if (seq === roomFetchSeqRef.current && !preserveLoadingForRecovery) setLoading(false);
      roomFetchInFlightRef.current = false;
      if (roomFetchPendingRef.current) {
        roomFetchPendingRef.current = false;
        const pendingOptions = roomFetchPendingOptionsRef.current || {};
        roomFetchPendingOptionsRef.current = null;
        clearTimeout(roomFetchRetryTimerRef.current);
        roomFetchRetryTimerRef.current = setTimeout(() => fetchRooms(pendingOptions), 180);
      }
    }
  };

  const retryRoomsNow = () => {
    clearTimeout(roomFetchRecoveryTimerRef.current);
    roomLoadFailureCountRef.current = 0;
    setLoadError('');
    setLoading(true);
    fetchRooms({ reset: true });
  };

  const refreshRooms = async () => {
    setRefreshingRooms(true);
    try {
      await fetchRooms();
    } finally {
      setRefreshingRooms(false);
    }
  };

  const loadOlderRooms = async () => {
    if (!roomPageInfo.hasMore || loadingOlderRooms) return;
    setLoadingOlderRooms(true);
    try {
      await fetchRooms({ append: true, cursor: roomPageInfo.nextCursor });
    } finally {
      setLoadingOlderRooms(false);
    }
  };

  // Keep paging in the background until the current tenant's entire chat
  // history is available. This preserves the explicit button as a fallback,
  // while removing the old 160-chat ceiling from normal admin use.
  useEffect(() => {
    if (isLocalDemoChat() || !roomPageInfo.hasMore || loadingOlderRooms) return undefined;
    const timer = window.setTimeout(() => {
      loadOlderRooms();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [roomPageInfo.hasMore, roomPageInfo.nextCursor, rooms.length, loadingOlderRooms]);

  useEffect(() => {
    const term = search.trim();
    if (term && term.length < 2) return undefined;
    const timer = window.setTimeout(() => {
      fetchRooms({ reset: true });
    }, term ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [search, selectedTenantId, isMasterAdmin, agentConversationMode]);

  useEffect(() => {
    if (isLocalDemoChat()) return undefined;
    const timer = window.setInterval(() => {
      const term = search.trim();
      if (term && term.length < 2) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRooms();
    }, 20000);
    return () => window.clearInterval(timer);
  }, [search, selectedTenantId, isMasterAdmin, agentConversationMode]);

  useEffect(() => {
    if (!isMasterAdmin) return;
    setSelectedRoom(null);
    setMessages([]);
    setRooms([]);
    setLoading(true);
    fetchRooms({ reset: true });
  }, [agentConversationMode, selectedTenantId, isMasterAdmin]);

  const fetchFavorites = async () => {
    try {
      if (isLocalDemoChat()) {
        setFavorites(JSON.parse(localStorage.getItem('tl_chat_favorites') || '[]'));
        return;
      }
      if (isMasterAdmin && !selectedTenantId) {
        setFavorites([]);
        return;
      }
      const params = new URLSearchParams();
      if (selectedTenantId) params.set('tenant_id', selectedTenantId);
      const res = await liveChatApi.get(`/api/chat/favorites${params.toString() ? `?${params.toString()}` : ''}`);
      const remoteIds = res.data?.important_player_ids || res.data?.favorites || [];
      setFavorites(remoteIds);

      let localIds = [];
      try { localIds = JSON.parse(localStorage.getItem('tl_chat_favorites') || '[]').filter(Boolean); } catch {}
      const migrationKey = `tl_chat_favorites_migrated_${selectedTenantId || 'tenant'}`;
      const shouldMigrate = localIds.length > 0 && !localStorage.getItem(migrationKey) && (!isMasterAdmin || selectedTenantId);
      if (shouldMigrate) {
        const missingIds = localIds.filter(id => !remoteIds.includes(id));
        if (missingIds.length) {
          setFavorites([...new Set([...remoteIds, ...missingIds])]);
          await Promise.allSettled(missingIds.map(id => liveChatApi.post(
            `/api/chat/favorites/${encodeURIComponent(id)}`,
            { tenant_id: selectedTenantId || undefined },
          )));
        }
        localStorage.setItem(migrationKey, '1');
      }
    } catch {
      setFavorites([]);
    }
  };

  const fetchConfiguredAgents = async () => {
    try {
      if (isLocalDemoChat()) {
        setConfiguredAgents([{
          id: 'demo-agent-anna',
          name: 'ANNA',
          title: 'Live Agent',
          enabled: true,
          avatar_url: '',
        }]);
        return;
      }
      const selectedTenant = tenantOptions.find(tenant => tenant.id === selectedTenantId);
      if (isMasterAdmin) {
        setConfiguredAgents(Array.isArray(selectedTenant?.branding?.chat_agents) ? selectedTenant.branding.chat_agents : []);
        return;
      }
      const res = await api.get('/api/admin/my-tenant');
      const agents = res.data?.tenant?.branding?.chat_agents;
      setConfiguredAgents(Array.isArray(agents) ? agents : []);
    } catch {
      setConfiguredAgents([]);
    }
  };

  const fetchTenantOptions = async () => {
    if (!isMasterAdmin) return;
    try {
      if (isLocalDemoChat()) {
        setTenantOptions([{ id: 'demo-tenant', name: 'White Room Demo', slug: 'demo' }]);
        setSelectedTenantId('demo-tenant');
        return;
      }
      const res = await api.get('/api/admin/tenants');
      const items = res.data?.items || res.data?.tenants || [];
      const tenants = items.filter(tenant => tenant?.id);
      setTenantOptions(tenants);
      setSelectedTenantId(current => current || tenants[0]?.id || '');
    } catch {
      setTenantOptions([]);
    }
  };

  const bulkRecallPlayerLabel = (player = {}) => (
    player.full_name
    || player.username
    || player.email
    || player.id
    || 'Player'
  );

  const searchBulkRecallPlayers = async () => {
    const term = bulkRecallSearch.trim();
    if (term.length < 2) {
      toast.error('Type at least 2 characters');
      return;
    }
    setBulkRecallSearching(true);
    setBulkRecallResult(null);
    try {
      if (isLocalDemoChat()) {
        const demoPlayer = {
          id: DEMO_ROOM.player_id,
          tenant_id: DEMO_ROOM.tenant_id,
          full_name: DEMO_ROOM.chat_display_name || DEMO_ROOM.player_name,
          username: DEMO_ROOM.player_name,
          email: DEMO_ROOM.player_email,
        };
        setBulkRecallResults([demoPlayer]);
        return;
      }
      const params = new URLSearchParams({ q: term, limit: '20' });
      if (selectedTenantId) params.set('tenant_id', selectedTenantId);
      const res = await liveChatApi.get(`/api/chat/staff/players/search?${params.toString()}`);
      setBulkRecallResults(res.data?.players || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not search players');
    } finally {
      setBulkRecallSearching(false);
    }
  };

  const addBulkRecallPlayer = (player) => {
    if (!player?.id) return;
    setBulkRecallSelected(prev => (
      prev.some(item => item.id === player.id) ? prev : [...prev, player]
    ));
  };

  const removeBulkRecallPlayer = (playerId) => {
    setBulkRecallSelected(prev => prev.filter(player => player.id !== playerId));
  };

  const sendBulkRecall = async () => {
    const body = bulkRecallMessage.trim();
    if (!body) {
      toast.error('Write a message first');
      return;
    }
    if (bulkRecallMode === 'selected' && bulkRecallSelected.length === 0) {
      toast.error('Choose at least one player');
      return;
    }

    setBulkRecallSending(true);
    setBulkRecallResult(null);
    try {
      if (isLocalDemoChat()) {
        const result = {
          sent_count: bulkRecallMode === 'all_chatted'
            ? 1
            : bulkRecallMode === 'favorites'
              ? favorites.length
              : bulkRecallSelected.length,
          skipped_count: 0,
          failed_count: 0,
        };
        setBulkRecallResult(result);
        toast.success(`Bulk recall sent to ${result.sent_count} player${result.sent_count === 1 ? '' : 's'}`);
        return;
      }
      const payload = {
        body,
        mode: bulkRecallMode,
        delay_ms: 50,
      };
      const selectedBulkAgent = bulkRecallAgentOptions.find(option => option.key === bulkRecallAgentKey);
      if (selectedBulkAgent) {
        payload.target_agent_id = selectedBulkAgent.id || null;
        payload.target_agent_name = selectedBulkAgent.name || null;
      }
      if (selectedTenantId) payload.tenant_id = selectedTenantId;
      if (bulkRecallMode === 'selected') {
        payload.recipients = bulkRecallSelected.map(player => ({ player_id: player.id }));
      }
      const res = await liveChatApi.post('/api/chat/staff/bulk-recall', payload);
      const result = res.data || {};
      setBulkRecallResult(result);
      toast.success(`Bulk recall sent to ${result.sent_count || 0} player${Number(result.sent_count || 0) === 1 ? '' : 's'}`);
      fetchRooms();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bulk recall failed');
    } finally {
      setBulkRecallSending(false);
    }
  };

  const toggleFavorite = async (e, playerId) => {
    e.stopPropagation();
    if (!playerId) return;
    try {
      const wasFavorite = favorites.includes(playerId);
      const next = wasFavorite ? favorites.filter(id => id !== playerId) : [...favorites, playerId];
      setFavorites(next);
      setRooms(prev => prev.map(room => (
        room.player_id === playerId ? { ...room, is_important: !wasFavorite } : room
      )));
      setSelectedRoom(prev => (
        prev?.player_id === playerId ? { ...prev, is_important: !wasFavorite } : prev
      ));
      if (isLocalDemoChat()) {
        localStorage.setItem('tl_chat_favorites', JSON.stringify(next));
      } else {
        const params = new URLSearchParams();
        if (selectedTenantId) params.set('tenant_id', selectedTenantId);
        if (wasFavorite) {
          await liveChatApi.delete(`/api/chat/favorites/${encodeURIComponent(playerId)}${params.toString() ? `?${params.toString()}` : ''}`);
        } else {
          await liveChatApi.post(`/api/chat/favorites/${encodeURIComponent(playerId)}`, { tenant_id: selectedTenantId || undefined });
        }
      }
    } catch {
      toast.error('Failed to update important marker');
      fetchFavorites();
      fetchRooms();
    }
  };

  const fetchMessages = async (roomId, options = {}) => {
    const seq = ++messageLoadSeqRef.current;
    const loadingOlder = Boolean(options.loadOlder && options.before);
    if (loadingOlder) {
      setLoadingOlderMessages(true);
    } else if (!options.quiet) {
      setLoadingMessages(true);
      setRefreshingMessages(Boolean(options.refreshing));
    }
    try {
      if (isLocalDemoChat()) {
        const room = { ...demoRoomFromMessages(), staff_unread: 0 };
        const nextMessages = readDemoMessages();
        setMessages(nextMessages);
        setSelectedRoom(prev => (prev?.id === roomId ? { ...prev, ...room } : room));
        setRooms(prev => (prev.length ? prev.map(r => r.id === roomId ? { ...r, ...room } : r) : [room]));
        setAiDraft(demoAiDraftFromMessages(nextMessages));
        scrollToBottom(true);
        return;
      }
      const scrollEl = messagesPaneRef.current;
      const shouldPinToRecent = forceNextMessageScrollRef.current;
      const preserveScroll = Boolean(
        scrollEl
        && !forceNextMessageScrollRef.current
        && (userScrollingMessagesRef.current || !isMessagePanePinnedToBottom())
      );
      const previousScrollTop = scrollEl?.scrollTop || 0;
      const previousScrollHeight = scrollEl?.scrollHeight || 0;
      const requestedAgentFilter = options.agentFilter || agentFilterRef.current;
      const selectedAgent = getAgentOption(requestedAgentFilter);
      const params = new URLSearchParams();
      params.set('limit', String(options.limit || MESSAGE_PAGE_LIMIT));
      if (options.before) params.set('before', options.before);
      if (selectedAgent?.id) params.set('agent_id', selectedAgent.id);
      if (selectedAgent?.name) params.set('agent_name', selectedAgent.name);
      // Direct rooms can live on another tenant's chat source.  Always carry
      // the selected room's tenant so a master admin reads that source rather
      // than a colliding local room with the same conversation id.
      const roomTenantId = selectedRoomRef.current?.id === roomId
        ? selectedRoomRef.current?.tenant_id
        : selectedTenantId;
      if (isMasterAdmin && roomTenantId) params.set('tenant_id', roomTenantId);
      const query = params.toString();
      const unreadSnapshotVersion = unreadStateRef.current.version();
      const res = await liveChatApi.get(`/api/chat/conversations/${roomId}/messages${query ? `?${query}` : ''}`);
      if (seq !== messageLoadSeqRef.current || selectedRoomRef.current?.id !== roomId
        || requestedAgentFilter !== agentFilterRef.current) return;
      const incomingMessages = res.data.messages || [];
      unreadStateRef.current.rememberMessages(incomingMessages);
      const conversation = res.data.conversation
        ? unreadStateRef.current.snapshot(res.data.conversation, unreadSnapshotVersion) : null;
      setLoadedMessageContext({ roomId, agentFilter: requestedAgentFilter });
      const hasMoreMessages = Boolean(res.data.has_more);
      if (loadingOlder) {
        setMessages(prev => mergeChatMessageList(incomingMessages, prev));
      } else {
        setMessages(incomingMessages.slice(-MESSAGE_MEMORY_LIMIT));
      }
      setHasOlderMessages(hasMoreMessages);
      if ((preserveScroll || loadingOlder) && scrollEl) {
        requestAnimationFrame(() => {
          const heightDelta = scrollEl.scrollHeight - previousScrollHeight;
          scrollEl.scrollTop = previousScrollTop + heightDelta;
        });
      } else if (shouldPinToRecent) {
        scrollToBottom(true);
      }
      if (conversation) {
        setSelectedRoom(prev => (prev?.id === roomId ? { ...prev, ...conversation } : prev));
      }
      if (!options.quiet && !loadingOlder) fetchAiDraft(roomId, { quiet: true });
      if (conversation && (options.markRead || isConversationVisible(roomId))) {
        const remainingUnread = Number(conversation.staff_unread || 0);
        setRooms(prev => prev.map(r => r.id === roomId ? { ...r, staff_unread: remainingUnread } : r));
      }
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      const activeRoom = selectedRoomRef.current;
      // A player can have a private referred-agent thread and a separate site
      // support thread. If a stale/direct URL selected the private one for a
      // tenant operator, resolve the accessible support thread automatically.
      if ([403, 404].includes(status) && activeRoom?.id === roomId && activeRoom?.player_id) {
        try {
          const payload = { player_id: activeRoom.player_id };
          if (activeRoom.tenant_id) payload.tenant_id = activeRoom.tenant_id;
          const direct = await liveChatApi.post('/api/chat/staff/conversations/direct', payload);
          const replacement = direct.data?.conversation;
          if (replacement?.id && replacement.id !== roomId && selectedRoomRef.current?.id === roomId) {
            setRooms(prev => sortRooms([replacement, ...prev.filter(item => item.id !== roomId && item.id !== replacement.id)]));
            setSelectedRoom(replacement);
            fetchMessages(replacement.id, { markRead: isConversationVisible(replacement.id), quiet: true, retry: true });
            return;
          }
        } catch {}
      }
      // Keep the selected room visible on an access error.  Removing it left
      // mobile operators in a blank "Select a conversation" pane and hid the
      // useful error state, especially while a remote tenant source retried.
      if (status === 403 && activeRoom?.id === roomId) {
        if (!options.quiet) {
          const detail = error?.response?.data?.detail || 'You do not have access to this conversation';
          toast.error(typeof detail === 'string' ? detail : 'You do not have access to this conversation');
        }
        return;
      }
      if (!options.quiet) {
        const detail = error?.response?.data?.detail || error?.message;
        toast.error(typeof detail === 'string' && detail ? detail : 'Could not load chat messages');
      }

      const canRetry = !loadingOlder && !options.retry && activeRoom?.id === roomId;
      if (canRetry) {
        setTimeout(() => {
          if (selectedRoomRef.current?.id === roomId) {
            fetchMessages(roomId, { ...options, quiet: true, retry: true });
          }
        }, 800);
      }

    }
    finally {
      if (seq === messageLoadSeqRef.current) {
        setLoadingMessages(false);
        setRefreshingMessages(false);
        setLoadingOlderMessages(false);
      }
    }
  };

  const loadOlderMessages = () => {
    if (!selectedRoom?.id || !messages.length || loadingOlderMessages) return;
    const before = messages[0]?.created_at;
    if (!before) return;
    fetchMessages(selectedRoom.id, { before, loadOlder: true, quiet: true });
  };

  const notifyIncomingPlayerMessage = (message) => {
    const title = message.sender_name || 'Player';
    const parsed = parseAgentTagFromBody(message.body || '');
    const body = parsed.body || (message.attachments?.length ? 'Sent a photo' : 'New message');
    toast.message(`${title}: ${body}`);
    try { navigator.vibrate?.(80); } catch {}
    try {
      const bridge = window.AndroidStaffChat;
      if (bridge?.showNotification) {
        bridge.requestNotifications?.();
        bridge.showNotification(title, body);
      } else if (bridge?.notify) {
        bridge.requestNotifications?.();
        bridge.notify(title, body);
      }
    } catch {}
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: resolveMediaUrl(message.sender_avatar_url || brandAvatarUrl) || undefined,
          tag: message.conversation_id,
          silent: false,
        });
      }
    } catch {}
  };

  const isConversationVisible = (roomId) => {
    if (!chatRouteVisibleRef.current) return false;
    if (!roomId || selectedRoomRef.current?.id !== roomId) return false;
    const desktop = window.matchMedia?.('(min-width: 768px)').matches;
    return document.visibilityState === 'visible' && (desktop || mobileChatOpenRef.current);
  };

  async function persistConversationRead(roomId) {
    if (!roomId || isLocalDemoChat() || !isConversationVisible(roomId)) return;
    const rendered = renderedReadRef.current;
    if (rendered?.roomId !== roomId || rendered.agentFilter !== agentFilterRef.current) return;
    const signature = JSON.stringify(rendered.payload);
    const streamKey = `${roomId}|${rendered.agentFilter}`;
    if (completedReadAckRef.current.get(streamKey) === signature) return;
    if (readAckInFlightRef.current.has(roomId)) {
      readAckPendingRef.current.add(roomId);
      return;
    }
    readAckInFlightRef.current.add(roomId);
    const unreadSnapshotVersion = unreadStateRef.current.version();
    try {
      const room = selectedRoomRef.current?.id === roomId
        ? selectedRoomRef.current
        : roomsRef.current.find(item => item.id === roomId);
      const params = new URLSearchParams();
      if (isMasterAdmin && room?.tenant_id) params.set('tenant_id', room.tenant_id);
      const query = params.toString();
      const res = await liveChatApi.post(`/api/chat/conversations/${roomId}/read${query ? `?${query}` : ''}`, rendered.payload);
      const remainingUnread = confirmedStaffUnread(res.data);
      completedReadAckRef.current.set(streamKey, signature);
      readAckFailuresRef.current.delete(streamKey);
      unreadStateRef.current.confirmRead(roomId, rendered.payload.rendered_message_ids);
      // An event received during this request may be newer than its response.
      // Refresh the authoritative inbox instead of hiding a new unread message.
      if (unreadStateRef.current.changedSince(roomId, unreadSnapshotVersion)) {
        fetchRooms();
        return;
      }
      unreadStateRef.current.update(roomId, remainingUnread);
      setRooms(prev => prev.map(item => (
        item.id === roomId ? { ...item, staff_unread: remainingUnread } : item
      )));
      setSelectedRoom(prev => (
        prev?.id === roomId ? { ...prev, staff_unread: remainingUnread } : prev
      ));
    } catch (error) {
      console.warn('Could not persist conversation read state', error);
      const previous = readAckFailuresRef.current.get(streamKey);
      const attempt = previous?.signature === signature ? previous.attempt + 1 : 1;
      readAckFailuresRef.current.set(streamKey, { signature, attempt });
      const delay = [1000, 2000, 4000][attempt - 1];
      if (isTransientChatLoadError(error) && delay && isConversationVisible(roomId)
        && renderedReadRef.current?.agentFilter === rendered.agentFilter) {
        acknowledgeConversationRead(roomId, { delay });
      }
    } finally {
      readAckInFlightRef.current.delete(roomId);
      if (readAckPendingRef.current.delete(roomId)) {
        acknowledgeConversationRead(roomId);
      }
    }
  }

  function acknowledgeConversationRead(roomId, { immediate = false, delay = 140 } = {}) {
    if (!roomId || isLocalDemoChat()) return;
    const existing = readAckTimersRef.current.get(roomId);
    if (existing) clearTimeout(existing);
    readAckTimersRef.current.delete(roomId);
    if (immediate) {
      persistConversationRead(roomId);
      return;
    }
    const timer = window.setTimeout(() => {
      readAckTimersRef.current.delete(roomId);
      persistConversationRead(roomId);
    }, delay);
    readAckTimersRef.current.set(roomId, timer);
  }

  const openRoomEditor = () => {
    if (!selectedRoom) return;
    setRoomNameDraft(visiblePlayerName(selectedRoom));
    setRoomLabelDrafts(roomLabels(selectedRoom));
    setRoomLabelInput('');
    setEditingRoomName(true);
  };

  const openWorkspaceMenu = () => {
    window.dispatchEvent(new Event(OPEN_ADMIN_WORKSPACE_MENU_EVENT));
  };

  const closeRoomEditor = () => {
    setRoomNameDraft(visiblePlayerName(selectedRoom || {}));
    setRoomLabelDrafts(roomLabels(selectedRoom || {}));
    setRoomLabelInput('');
    setEditingRoomName(false);
  };

  const addRoomLabelDraft = () => {
    const label = roomLabelInput.replace(/\s+/g, ' ').trim().slice(0, MAX_ROOM_LABEL_LENGTH).trim();
    if (!label) return;
    const next = normalizeRoomLabels([...roomLabelDrafts, label]);
    if (next.length === roomLabelDrafts.length && roomLabelDrafts.some(item => item.toLowerCase() === label.toLowerCase())) {
      setRoomLabelInput('');
      return;
    }
    if (roomLabelDrafts.length >= MAX_ROOM_LABELS) {
      toast.error(`Maximum ${MAX_ROOM_LABELS} labels`);
      return;
    }
    setRoomLabelDrafts(next);
    setRoomLabelInput('');
  };

  const saveRoomDisplayName = async () => {
    if (!selectedRoom?.id) return;
    const nextName = roomNameDraft.trim();
    const pendingLabel = roomLabelInput.replace(/\s+/g, ' ').trim();
    const nextLabels = normalizeRoomLabels(pendingLabel ? [...roomLabelDrafts, pendingLabel] : roomLabelDrafts);
    const optimisticRoom = { ...selectedRoom, chat_display_name: nextName || null, labels: nextLabels };
    setSelectedRoom(optimisticRoom);
    setRooms(prev => prev.map(room => (
      room.id === selectedRoom.id ? { ...room, chat_display_name: nextName || null, labels: nextLabels } : room
    )));
    setEditingRoomName(false);
    try {
      const res = await liveChatApi.post(`/api/chat/conversations/${selectedRoom.id}/name`, {
        chat_display_name: nextName || null,
        labels: nextLabels,
      });
      setSelectedRoom(res.data.conversation);
      setRooms(prev => prev.map(room => (
        room.id === selectedRoom.id ? { ...room, ...res.data.conversation } : room
      )));
    } catch (error) {
      setSelectedRoom(selectedRoom);
      setRooms(prev => prev.map(room => (
        room.id === selectedRoom.id ? { ...room, chat_display_name: selectedRoom.chat_display_name || null, labels: roomLabels(selectedRoom) } : room
      )));
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save chat details');
    }
  };

  const fetchAiDraft = async (roomId, options = {}) => {
    if (!roomId) return;
    const now = Date.now();
    const lastFetch = aiDraftFetchRef.current;
    if (options.quiet && lastFetch.roomId === roomId && now - lastFetch.at < 8000) return;
    aiDraftFetchRef.current = { roomId, at: now };
    if (!options.quiet) setAiDraftLoading(true);
    try {
      if (isLocalDemoChat()) {
        setAiDraft(demoAiDraftFromMessages());
        return;
      }
      const res = await liveChatApi.get(`/api/chat/conversations/${roomId}/ai/draft`);
      if (selectedRoomRef.current?.id !== roomId) return;
      setAiDraft(res.data.draft || null);
    } catch {
      if (!options.quiet) setAiDraft(null);
    } finally {
      if (!options.quiet) setAiDraftLoading(false);
    }
  };

  const useAiDraft = async () => {
    const text = (aiDraft?.draft_text || '').trim();
    if (!text) return;
    composer.writeText(text);
    inputRef.current?.focus({ preventScroll: true });
    if (isLocalDemoChat()) {
      setAiDraft(prev => prev ? { ...prev, feedback_action: 'used' } : prev);
      return;
    }
    try {
      await liveChatApi.post(`/api/chat/ai/drafts/${aiDraft.id}/feedback`, {
        action: 'used',
        final_text: text,
      });
    } catch {}
  };

  const markAiDraft = async (action) => {
    if (!aiDraft?.id) return;
    if (isLocalDemoChat()) {
      setAiDraft(prev => prev ? { ...prev, feedback_action: action } : prev);
      toast.success(action === 'bad' ? 'AI draft marked bad' : 'AI draft saved for style');
      return;
    }
    try {
      await liveChatApi.post(`/api/chat/ai/drafts/${aiDraft.id}/feedback`, {
        action,
        final_text: composer.readText().trim() || aiDraft.draft_text || '',
      });
      toast.success(action === 'bad' ? 'AI draft marked bad' : 'AI draft saved for style');
      setAiDraft(prev => prev ? { ...prev, feedback_action: action } : prev);
    } catch {
      toast.error('Could not save AI feedback');
    }
  };

  const sendTypingState = (isTyping) => {
    if (isLocalDemoChat()) return;
    if (!selectedRoomRef.current?.id || wsRef.current?.readyState !== 1) return;
    const replyAgent = getAgentOption(agentFilterRef.current);
    const agentName = replyAgent?.name || agentNameForRoom(selectedRoomRef.current) || user?.username || 'Agent';
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      conversation_id: selectedRoomRef.current.id,
      agent_name: agentName,
      is_typing: Boolean(isTyping),
    }));
  };

  const stopTypingPulse = () => {
    clearTimeout(typingStopTimerRef.current);
    if (!typingPulseTimerRef.current) return;
    clearInterval(typingPulseTimerRef.current);
    typingPulseTimerRef.current = null;
    sendTypingState(false);
  };

  const startTypingPulse = (value) => {
    if (!String(value || '').trim()) {
      stopTypingPulse();
      return;
    }
    if (!typingPulseTimerRef.current) {
      sendTypingState(true);
      typingPulseTimerRef.current = setInterval(() => sendTypingState(true), 850);
    }
    clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(stopTypingPulse, 1700);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    composer.onChange(event);
    startTypingPulse(value);
  };

  const sendMessage = async () => {
    const msgText = composer.readText().trim();
    if ((!msgText && !photo) || !selectedRoom || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);

    const clientMessageId = `staff_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const tempId = `temp_${clientMessageId}`;
    const replyAgent = getAgentOption(agentFilterRef.current);
    const selectedAgentName = replyAgent?.name || agentNameForRoom(selectedRoom);
    const selectedAgentId = replyAgent?.id || selectedRoom.last_target_agent_id || selectedRoom.preferred_agent_id || null;
    const selectedAgentTitle = replyAgent?.title || selectedRoom.last_target_agent_title || selectedRoom.preferred_agent_title || '';
    const selectedAgentAvatar = replyAgent?.avatar_url || selectedRoom.last_target_agent_avatar_url || selectedRoom.preferred_agent_avatar_url || '';
    const selectedAgentBio = replyAgent?.bio || selectedRoom.last_target_agent_bio || selectedRoom.preferred_agent_bio || '';
    const activeReply = replyToMessage?.id ? replyToMessage : null;

    const photoPreviewUrl = photo ? URL.createObjectURL(photo) : '';

    // Optimistic add
    forceNextMessageScrollRef.current = true;
    setMessages(prev => [...prev, {
      id: tempId,
      client_message_id: clientMessageId,
      sender_id: user?.id,
      sender_name: selectedAgentName || (isAgentPortal ? 'Agent' : (user?.username || 'Support')),
      sender_role: 'staff',
      body: msgText,
      attachments: photo ? [{ id: 'pending', filename: photo.name, url: photoPreviewUrl, preview_url: photoPreviewUrl }] : [],
      created_at: new Date().toISOString(),
      reply_to_message_id: activeReply?.id || null,
      reply_to: activeReply || null,
      target_agent_id: selectedAgentId,
      target_agent_name: selectedAgentName || null,
      target_agent_title: selectedAgentTitle,
      target_agent_avatar_url: selectedAgentAvatar,
      target_agent_bio: selectedAgentBio,
    }]);
    composer.writeText('');
    setReplyToMessage(null);
    stopTypingPulse();
    inputRef.current?.focus({ preventScroll: true });

    if (isLocalDemoChat()) {
      setTimeout(() => {
        const finalMessage = {
          id: `demo_staff_${Date.now()}`,
          client_message_id: clientMessageId,
          sender_id: user?.id,
          sender_name: selectedAgentName || 'ANNA',
          sender_role: 'staff',
          body: msgText,
          attachments: photo ? [{ id: 'pending', filename: photo.name, url: photoPreviewUrl, preview_url: photoPreviewUrl }] : [],
          created_at: new Date().toISOString(),
          delivery_status: 'seen',
          reply_to_message_id: activeReply?.id || null,
          reply_to: activeReply || null,
          target_agent_id: selectedAgentId,
          target_agent_name: selectedAgentName || 'ANNA',
          target_agent_title: selectedAgentTitle,
          target_agent_avatar_url: selectedAgentAvatar,
          target_agent_bio: selectedAgentBio,
        };
        writeDemoMessages(prev => [...prev.filter(m => m.id !== tempId), finalMessage].slice(-MESSAGE_MEMORY_LIMIT));
        setMessages(readDemoMessages());
        setRooms(prev => sortRooms(prev.map(room => (
          room.id === selectedRoom.id
            ? { ...room, last_message: msgText || 'Photo', last_sender_role: 'staff', last_sender_name: selectedAgentName || 'ANNA', last_message_at: new Date().toISOString(), updated_at: new Date().toISOString() }
            : room
        ))));
        setPhoto(null);
        setEmojiOpen(false);
        sendingRef.current = false;
        setSending(false);
      }, 180);
      return;
    }

    try {
      let attachmentIds = [];
      if (photo) {
        attachmentIds = [(await uploadLiveChatPhoto(photo)).id];
      }
      const messageParams = new URLSearchParams();
      if (isMasterAdmin && selectedRoom.tenant_id) messageParams.set('tenant_id', selectedRoom.tenant_id);
      const messagePath = `/api/chat/conversations/${selectedRoom.id}/messages${messageParams.toString() ? `?${messageParams.toString()}` : ''}`;
      const res = await liveChatApi.post(messagePath, {
        body: msgText,
        attachment_ids: attachmentIds,
        client_message_id: clientMessageId,
        reply_to_message_id: activeReply?.id || null,
        target_agent_id: selectedAgentId,
        target_agent_name: selectedAgentName || null,
        target_agent_title: selectedAgentTitle,
        target_agent_avatar_url: selectedAgentAvatar,
        target_agent_bio: selectedAgentBio,
      });
      setMessages(prev => mergeChatMessage(prev, res.data.message, MESSAGE_MEMORY_LIMIT));
      setSelectedRoom(prev => (
        prev?.id === selectedRoom.id
          ? { ...prev, newly_joined: false, conversation_started_at: res.data.message?.created_at || prev.conversation_started_at }
          : prev
      ));
      setRooms(prev => sortRooms(prev.map(room => (
        room.id === selectedRoom.id
          ? { ...room, newly_joined: false, conversation_started_at: res.data.message?.created_at || room.conversation_started_at }
          : room
      ))));
      setPhoto(null);
      setEmojiOpen(false);
      if (photoRef.current) photoRef.current.value = '';
      // Retry only the confirmed history actually rendered in this stream.
      // A reply must never blanket-clear other agents' or unseen messages.
      if (isConversationVisible(selectedRoom.id)) {
        const streamKey = `${selectedRoom.id}|${agentFilterRef.current}`;
        readAckFailuresRef.current.delete(streamKey);
        acknowledgeConversationRead(selectedRoom.id);
      }
    } catch {
      toast.error('Failed to send');
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      sendingRef.current = false;
      setSending(false);
    }
  };

  const isMessagePanePinnedToBottom = (threshold = 8) => {
    const el = messagesPaneRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const updateMessageScrollLock = () => {
    if (ignoreProgrammaticMessageScrollRef.current) return;
    userScrollingMessagesRef.current = true;
    clearTimeout(messageScrollTimerRef.current);
    messageScrollTimerRef.current = setTimeout(() => {
      userScrollingMessagesRef.current = false;
    }, 900);
  };

  const pinMessagesPaneToRecent = () => {
    const el = messagesPaneRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const scrollToBottom = (force = false) => {
    if (!force && !forceNextMessageScrollRef.current) return;
    const finishProgrammaticScroll = () => {
      forceNextMessageScrollRef.current = false;
      setTimeout(() => {
        ignoreProgrammaticMessageScrollRef.current = false;
      }, 120);
    };

    messageScrollPinTimersRef.current.forEach(clearTimeout);
    messageScrollPinTimersRef.current = [];
    ignoreProgrammaticMessageScrollRef.current = true;
    userScrollingMessagesRef.current = false;
    [0, 40, 120, 260, 520, 900].forEach((delay, index, steps) => {
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          pinMessagesPaneToRecent();
          if (index === steps.length - 1) finishProgrammaticScroll();
        });
      }, delay);
      messageScrollPinTimersRef.current.push(timer);
    });
  };

  const addEmoji = (emoji) => {
    composer.writeText(value => `${value}${emoji}`);
    inputRef.current?.focus({ preventScroll: true });
  };

  const reactToMessage = async (messageId, emoji) => {
    if (messageId.startsWith('temp_')) return;
    try {
      const res = await liveChatApi.post(`/api/chat/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      setActiveReactionId(null);
      setExpandedReactionId(null);
    } catch {
      toast.error('Could not add reaction');
    }
  };

  const closeMessageMenu = () => {
    editVersionRef.current += 1;
    clearTimeout(reactionTimerRef.current);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setActiveMessageMenuId(null);
    setMessageMenuMode('controls');
    setEditingMessageId(null);
    setEditingText('');
  };

  const openMessageMenu = (msg) => {
    if (String(msg.id || '').startsWith('temp_')) return;
    editVersionRef.current += 1;
    setActiveMessageMenuId(msg.id);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setMessageMenuMode('controls');
    setVisibleTimeId(msg.id);
    setEditingMessageId(null);
    setEditingText('');
  };

  const copyMessageText = async (msg) => {
    const text = parseAgentTagFromBody(msg.body || '').body.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
      closeMessageMenu();
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyRoomUsername = async (room, event) => {
    event?.stopPropagation?.();
    const username = playerUsername(room);
    if (!username) {
      toast.error('No username to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(username);
      toast.success('Username copied');
    } catch {
      toast.error('Could not copy username');
    }
  };

  const copySelectedPlayerId = async () => {
    const playerId = selectedRoomRef.current?.player_id;
    if (!playerId) return;
    try {
      await navigator.clipboard.writeText(playerId);
      toast.success('Player ID copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const startEditingMessage = (msg) => {
    if (!ownsChatMessage(msg, chatActorId)) return;
    editVersionRef.current += 1;
    setEditingMessageId(msg.id);
    setEditingText(parseAgentTagFromBody(msg.body || '').body);
  };

  const saveEditedMessage = async (messageId) => {
    const text = editingText.trim();
    const message = messages.find(item => item.id === messageId);
    if (!text || editInFlightRef.current || !ownsChatMessage(message, chatActorId)) return;
    // Keep legacy routing metadata when editing a migrated message's text.
    const tagged = String(message.body || '').startsWith(AGENT_SUBJECT_PREFIX);
    const body = tagged ? `${message.body.split('\n')[0]}\n\n${text}` : text;
    if (body.length > 5000) {
      toast.error('Message is too long. Shorten it before saving.');
      return;
    }
    if (isLocalDemoChat()) {
      const nextMessages = writeDemoMessages(prev => prev.map(item => (
        item.id === messageId ? { ...item, body, edited_at: new Date().toISOString() } : item
      )));
      setMessages(nextMessages);
      closeMessageMenu();
      return;
    }
    const roomId = selectedRoomRef.current?.id;
    const version = editVersionRef.current;
    editInFlightRef.current = true;
    setSavingEdit(true);
    try {
      const res = await liveChatApi.patch(`/api/chat/messages/${encodeURIComponent(messageId)}`, { body });
      if (selectedRoomRef.current?.id === roomId) {
        setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
        if (editVersionRef.current === version) closeMessageMenu();
      }
    } catch (error) {
      if (selectedRoomRef.current?.id === roomId && editVersionRef.current === version) {
        toast.error(error?.response?.status === 403
          ? 'Only the original sender can edit this message.'
          : 'Could not save your edit. Your text is kept—please try again.');
      }
    } finally {
      editInFlightRef.current = false;
      setSavingEdit(false);
    }
  };

  useEffect(() => { closeMessageMenu(); }, [selectedRoom?.id]);

  const deleteMessage = async (messageId) => {
    if (isLocalDemoChat()) {
      const nextMessages = writeDemoMessages(prev => prev.map(item => (
        item.id === messageId
          ? { ...item, body: '', attachments: [], reactions: {}, deleted: true, deleted_at: new Date().toISOString() }
          : item
      )));
      setMessages(nextMessages);
      closeMessageMenu();
      return;
    }
    try {
      const res = await liveChatApi.delete(`/api/chat/messages/${messageId}`);
      setMessages(prev => prev.map(item => item.id === messageId ? res.data.message : item));
      closeMessageMenu();
    } catch {
      toast.error('Could not delete message');
    }
  };

  const startReplyToMessage = (msg) => {
    if (!msg?.id || String(msg.id).startsWith('temp_') || msg.deleted) return;
    setReplyToMessage(replyPreviewForMessage(msg));
    closeMessageMenu();
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const beginMessageSwipe = (x, y, msg, event) => {
    if (isInteractiveMessageTarget(event?.target)) return;
    beginLongPress(msg.id);
    messageSwipeRef.current = {
      id: msg.id,
      x,
      y,
    };
  };

  const endMessageSwipe = (x, y, msg, event) => {
    cancelLongPress();
    const start = messageSwipeRef.current;
    messageSwipeRef.current = null;
    if (!start || start.id !== msg.id) return;
    const deltaX = x - start.x;
    const deltaY = Math.abs(y - start.y);
    if (Math.abs(deltaX) > 42 && deltaY < 38) {
      event.preventDefault();
      swipeActivatedRef.current = true;
      startReplyToMessage(msg);
      setTimeout(() => {
        swipeActivatedRef.current = false;
      }, 250);
    }
  };

  const handleMessageTouchStart = (event, msg) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    beginMessageSwipe(touch.clientX, touch.clientY, msg, event);
  };

  const handleMessageTouchEnd = (event, msg) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    endMessageSwipe(touch.clientX, touch.clientY, msg, event);
  };

  const handleMessagePointerDown = (event, msg) => {
    if (event.pointerType === 'mouse' || isInteractiveMessageTarget(event.target)) return;
    beginMessageSwipe(event.clientX, event.clientY, msg, event);
  };

  const handleMessagePointerUp = (event, msg) => {
    if (event.pointerType === 'mouse' || isInteractiveMessageTarget(event.target)) return;
    endMessageSwipe(event.clientX, event.clientY, msg, event);
  };

  const beginLongPress = (messageId) => {
    clearTimeout(reactionTimerRef.current);
    reactionTimerRef.current = setTimeout(() => {
      setActiveMessageMenuId(messageId);
      setMessageMenuMode('controls');
      setVisibleTimeId(messageId);
    }, 450);
  };

  const cancelLongPress = () => {
    clearTimeout(reactionTimerRef.current);
  };

  const closeReactionPicker = () => {
    closeMessageMenu();
  };

  const closeReactionsOnOutsideClick = (event) => {
    if (!activeReactionId && !activeMessageMenuId) return;
    const target = event.target;
    if (target?.closest?.('[data-chat-bubble], [data-reaction-tray], [data-message-controls], [data-message-actions]')) return;
    closeMessageMenu();
  };

  const openPhotoPreview = (item) => {
    setPhotoPreview(item);
    if (!previewHistoryRef.current) {
      window.history.pushState({ chatPhotoPreview: true }, '');
      previewHistoryRef.current = true;
    }
  };

  const closePhotoPreview = () => {
    if (previewHistoryRef.current) {
      previewHistoryRef.current = false;
      window.history.replaceState({ ...(window.history.state || {}), chatPhotoPreview: false }, '');
    }
    setPhotoPreview(null);
  };

  const toggleMessageTime = (messageId) => {
    setVisibleTimeId(current => current === messageId ? null : messageId);
  };

  const openMobileConversation = (room) => {
    if (!room?.id) return;
    const sameRoom = selectedRoomRef.current?.id === room.id;
    selectedRoomRef.current = { ...(selectedRoomRef.current || {}), ...room };
    if (!sameRoom) {
      messageLoadSeqRef.current += 1;
      setMessages([]);
      userScrollingMessagesRef.current = false;
      forceNextMessageScrollRef.current = true;
    }
    setVisibleTimeId(null);
    setActiveReactionId(null);
    setExpandedReactionId(null);
    setSelectedRoom(prev => (prev?.id === room.id ? { ...prev, ...room } : room));
    mobileChatOpenRef.current = true;
    setMobileChatOpen(true);
    if (window.matchMedia?.('(max-width: 767px)').matches && !mobileChatHistoryRef.current) {
      window.history.pushState({ ...(window.history.state || {}), chatConversationOpen: true }, '');
      mobileChatHistoryRef.current = true;
    }
  };

  const closeMobileConversation = () => {
    mobileChatOpenRef.current = false;
    setMobileChatOpen(false);
    if (mobileChatHistoryRef.current) {
      mobileChatHistoryRef.current = false;
      window.history.replaceState({ ...(window.history.state || {}), chatConversationOpen: false }, '');
    }
  };

  const openSearchPlayer = async (player) => {
    if (openingSearchPlayerRef.current) return;
    openingSearchPlayerRef.current = true;
    setOpeningSearchPlayerId(player.id);
    const workspace = searchWorkspaceRef.current;
    try {
      const room = await openDirectoryPlayerChat(player, activeChatTenantId);
      if (workspace !== searchWorkspaceRef.current) return;
      setRooms(prev => sortRooms([room, ...prev.filter(item => item.id !== room.id)]));
      setSearch('');
      openMobileConversation(room);
      setTimeout(() => inputRef.current?.focus(), 120);
    } catch (error) {
      if (workspace === searchWorkspaceRef.current) {
        toast.error(error?.response?.data?.detail || 'Could not open player chat. Please try again.');
      }
    } finally {
      openingSearchPlayerRef.current = false;
      setOpeningSearchPlayerId('');
    }
  };

  useEffect(() => {
    // AdminLayout keeps chat mounted on other pages. A player overview link
    // must not create/open a conversation or consume unread messages there.
    if (!isChatRoute) return undefined;
    const playerId = (searchParams.get('player_id') || searchParams.get('user_id') || '').trim();
    if (!playerId) return undefined;
    const tenantId = sourceTenantIdForChat((searchParams.get('tenant_id') || '').trim());
    const directKey = `${tenantId || 'auto'}:${playerId}`;
    if (handledDirectOpenRef.current === directKey) return undefined;

    if (isMasterAdmin && tenantId && selectedTenantId !== tenantId) {
      setSelectedTenantId(tenantId);
      return undefined;
    }

    let cancelled = false;
    const openDirectConversation = async () => {
      try {
        const payload = { player_id: playerId };
        const effectiveTenantId = tenantId || selectedTenantId;
        if (effectiveTenantId) payload.tenant_id = centralTenantIdForChat(effectiveTenantId);
        const res = await liveChatApi.post('/api/chat/staff/conversations/direct', payload);
        if (cancelled) return;
        const room = res.data?.conversation;
        if (!room?.id) throw new Error('Missing conversation');
        if (isMasterAdmin && room.tenant_id && !sameChatTenant(selectedTenantId, room.tenant_id)) {
          setSelectedTenantId(sourceTenantIdForChat(room.tenant_id));
          // Changing the master workspace intentionally clears the active
          // room.  Wait for that transition, then this effect will rerun in
          // the correct tenant and open the player instead of leaving the
          // mobile pane on "Select a conversation".
          handledDirectOpenRef.current = '';
          return;
        }
        handledDirectOpenRef.current = directKey;
        setRooms(prev => sortRooms([room, ...prev.filter(item => item.id !== room.id)]));
        openMobileConversation(room);
        setTimeout(() => inputRef.current?.focus(), 120);
        toast.success(`Ready to text ${visiblePlayerName(room)}`);
      } catch (error) {
        if (cancelled) return;
        handledDirectOpenRef.current = '';
        toast.error(error?.response?.data?.detail || 'Could not open player chat');
      }
    };
    openDirectConversation();
    return () => {
      cancelled = true;
    };
  }, [isChatRoute, isMasterAdmin, searchParams, selectedTenantId, visiblePlayerName]);

  const refreshActiveConversation = async () => {
    if (!selectedRoomRef.current?.id) return;
    setRefreshingMessages(true);
    await Promise.all([
      fetchRooms(),
      fetchMessages(selectedRoomRef.current.id, { refreshing: true }),
    ]);
    setRefreshingMessages(false);
  };

  const openPlayerDetails = async () => {
    const room = selectedRoomRef.current;
    if (!room?.player_id) return;
    const sourceIdentity = sourceChatPlayerIdentity(
      room.player_id, room.tenant_id || selectedTenantId || user?.tenant_id || '',
    );
    setHeaderMenuOpen(false);
    // Tenant admins should land on the existing full customer overview,
    // where wallet, payments, game accounts, ledger, and account controls live.
    // The agent portal retains its deliberately scoped in-chat preview.
    if (!isAgentPortal) {
      const params = new URLSearchParams({ player_id: sourceIdentity.playerId });
      const tenantId = sourceIdentity.tenantId;
      if (tenantId) params.set('tenant_id', tenantId);
      const basePath = window.location.pathname.startsWith('/survey')
        ? '/survey'
        : (tenantSlug ? `/${tenantSlug}/admin` : '/admin');
      navigate(`${basePath}/players?${params.toString()}`);
      return;
    }
    setDetailsOpen(true);
    if (playerDetailsPlayerId === room.player_id && playerDetails) return;
    setDetailsLoading(true);
    try {
      const res = await api.get(`/api/agent-earn/me/players/${encodeURIComponent(sourceIdentity.playerId)}/preview`);
      setPlayerDetails(res.data || null);
      setPlayerDetailsPlayerId(room.player_id);
    } catch (err) {
      setPlayerDetails(null);
      toast.error(err.response?.data?.detail || 'Could not load player details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleMessagesTouchStart = (event) => {
    if (messagesPaneRef.current?.scrollTop > 4) {
      touchStartYRef.current = null;
      return;
    }
    touchStartYRef.current = event.touches?.[0]?.clientY ?? null;
  };

  const handleMessagesTouchEnd = (event) => {
    if (touchStartYRef.current === null) return;
    const endY = event.changedTouches?.[0]?.clientY ?? touchStartYRef.current;
    const pulledDown = endY - touchStartYRef.current;
    touchStartYRef.current = null;
    if (pulledDown > 52) refreshActiveConversation();
  };

  const handleRoomsTouchStart = (event) => {
    if (roomsPaneRef.current?.scrollTop > 4) {
      roomsTouchStartYRef.current = null;
      return;
    }
    roomsTouchStartYRef.current = event.touches?.[0]?.clientY ?? null;
  };

  const handleRoomsTouchEnd = (event) => {
    if (roomsTouchStartYRef.current === null) return;
    const endY = event.changedTouches?.[0]?.clientY ?? roomsTouchStartYRef.current;
    const pulledDown = endY - roomsTouchStartYRef.current;
    roomsTouchStartYRef.current = null;
    if (pulledDown > 52) refreshRooms();
  };

  const deliveryStatusForMessage = (msg, isAdmin) => {
    if (!isAdmin) return null;
    if (String(msg.id || '').startsWith('temp_')) return 'Sending...';
    return msg.read_by_player ? 'Seen' : 'Delivered';
  };

  const renderReactionBadges = (msg, isAdmin) => {
    const entries = Object.entries(msg.reactions || {}).filter(([, users]) => users?.length).slice(-1);
    if (!entries.length) return null;
    return (
      <BubbleReactions>
        {entries.map(([emoji, users]) => (
          <button
            key={emoji}
            type="button"
            onClick={() => reactToMessage(msg.id, emoji)}
            className="h-5 min-w-5 px-1 rounded-full text-[13px] flex items-center justify-center gap-0.5"
            style={{
              background: isAdmin ? 'rgba(0,132,255,0.82)' : 'rgba(15,23,42,0.10)',
              color: isAdmin ? '#FFFFFF' : '#111827',
              boxShadow: 'none',
              border: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif'
            }}
          >
            <span>{emoji}</span>
            {users.length > 1 && <span className="text-[9px] opacity-75">{users.length}</span>}
          </button>
        ))}
      </BubbleReactions>
    );
  };

  const renderQuickReactions = (msg) => (
    activeMessageMenuId === msg.id && messageMenuMode === 'emoji' ? (
    <div className="mt-1 flex flex-col items-start gap-1.5" data-reaction-tray>
      <div className="flex gap-1 opacity-95 rounded-full bg-white px-1.5 py-1 shadow-xl">
        {QUICK_REACTIONS.map(emoji => (
          <button
            key={emoji}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={() => reactToMessage(msg.id, emoji)}
            className="w-7 h-7 rounded-full text-[14px] transition hover:scale-110"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
            aria-label={`React ${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={() => setExpandedReactionId(current => current === msg.id ? null : msg.id)}
          className="w-7 h-7 rounded-full text-[18px] leading-none transition hover:bg-black/5"
          style={{ color: '#111827' }}
          aria-label="More reactions"
        >
          +
        </button>
      </div>
      {expandedReactionId === msg.id && (
        <div className="grid max-h-32 w-56 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl bg-white p-1.5 shadow-2xl">
          {IPHONE_EMOJIS.map((emoji, index) => (
            <button
              key={`${emoji}-${index}`}
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={() => reactToMessage(msg.id, emoji)}
              className="h-7 rounded-xl text-[18px] transition hover:bg-black/5"
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
              aria-label={`React ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
    ) : null
	  );

  const renderMessageControls = (msg, isAdmin) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'controls' || msg.deleted) return null;
    return (
      <div
        className={`mt-1 flex items-center gap-1.5 ${isAdmin ? 'justify-end pr-3' : 'justify-start pl-3'}`}
        data-message-controls
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(msg.id);
            setMessageMenuMode('emoji');
            setExpandedReactionId(null);
          }}
          className="chat-message-control flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999 }}
          aria-label="Open reactions"
        >
          <SmilePlus className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveReactionId(null);
            setExpandedReactionId(null);
            setMessageMenuMode('actions');
          }}
          className="chat-message-control flex h-9 w-9 items-center justify-center rounded-full text-white shadow-xl transition hover:scale-105"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36, padding: 0, borderRadius: 9999 }}
          aria-label="Open message actions"
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderMessageActions = (msg, isAdmin) => {
    if (activeMessageMenuId !== msg.id || messageMenuMode !== 'actions' || msg.deleted) return null;
    const displayBody = parseAgentTagFromBody(msg.body || '').body;
    const isOwnStaffMessage = isAdmin && ownsChatMessage(msg, chatActorId);
    const canEdit = isOwnStaffMessage && Boolean(displayBody.trim());
    const canDelete = isOwnStaffMessage;
    const canCopy = Boolean(displayBody.trim());
    return (
      <div
        className={`mt-1 flex flex-col gap-1 ${isAdmin ? 'items-end' : 'items-start'}`}
        data-message-actions
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="w-40 overflow-hidden rounded-2xl bg-white py-1 shadow-xl">
          <button
            type="button"
            onClick={() => startReplyToMessage(msg)}
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
          >
            <Reply className="h-4 w-4" />
            Reply
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={() => deleteMessage(msg.id)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => startEditingMessage(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          )}
          {canCopy && (
            <button
              type="button"
              onClick={() => copyMessageText(msg)}
              className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
            >
              <Copy className="h-4 w-4" />
              Copy text
            </button>
          )}
        </div>
        {editingMessageId === msg.id && (
          <div className="flex w-[min(300px,78vw)] items-center gap-1 rounded-2xl bg-white p-1.5 shadow-xl">
            <input
              value={editingText}
              aria-label="Edit message"
              disabled={savingEdit}
              maxLength={5000}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveEditedMessage(msg.id);
                }
                if (e.key === 'Escape') closeMessageMenu();
              }}
              className="min-w-0 flex-1 rounded-full bg-gray-100 px-3 py-2 text-base text-gray-950 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              autoFocus
            />
            <button
              type="button"
              onClick={() => saveEditedMessage(msg.id)}
              disabled={savingEdit || !editingText.trim()}
              className="h-9 rounded-full bg-blue-700 px-3 text-xs font-bold text-white disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-blue-500 active:scale-[0.97]"
            >
              {savingEdit ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={closeMessageMenu} className="h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Cancel edit">
              <X className="mx-auto h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const brandAvatarUrl = resolveMediaUrl(theme?.logo_url || theme?.intro_logo_url || selectedRoom?.tenant_logo_url || '');
  const playerInitial = (name = '', email = '') => {
    const source = String(name || email || 'Player').trim();
    const firstName = source.split(/\s+/)[0] || source;
    return (firstName.charAt(0) || 'P').toUpperCase();
  };
  const avatarForMessage = (msg, isAdmin) => {
    if (isAdmin) return resolveMediaUrl(user?.avatar_url || brandAvatarUrl);
    return resolveMediaUrl(msg.sender_avatar_url || selectedRoom?.player_avatar_url || '');
  };

  const renderAvatar = (url, fallback, className = 'w-8 h-8') => (
    <div className={`${className} rounded-full shrink-0 flex items-center justify-center text-[11px] font-black overflow-hidden shadow`} style={{ background: '#fff', color: '#111827' }}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : fallback}
    </div>
  );

  const renderMessageAvatar = (url, fallback) => (
    <Avatar className="size-8 border border-white/10 bg-white text-[11px] font-black text-gray-950 shadow">
      {url && <AvatarImage src={url} alt="" className="object-cover" />}
      <AvatarFallback className="bg-white text-[11px] font-black text-gray-950">
        {fallback}
      </AvatarFallback>
    </Avatar>
  );

  const renderAgentPill = (name, prefix = 'Texted to agent') => {
    if (!name) return null;
    // In the agent portal every thread already belongs to the signed-in agent,
    // so naming them on each row and bubble is pure noise.
    if (isAgentPortal) return null;
    const accent = agentAccentForName(name);
    return (
      <span
        className="inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
        style={{ background: accent.bg, border: `1px solid ${accent.border}`, color: accent.color }}
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: accent.color, boxShadow: `0 0 8px ${accent.color}` }} />
        <span className="truncate">{prefix} {name}</span>
      </span>
    );
  };

  const renderRoomLabels = (room, className = '') => {
    const labels = roomLabels(room);
    if (!labels.length) return null;
    return (
      <div className={`flex max-w-full flex-wrap gap-1 ${className}`}>
        {labels.map(label => (
          <span key={label} className="inline-flex max-w-[150px] items-center rounded-full border border-cyan-300/30 bg-cyan-400/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.08em] text-cyan-100" title={label}>
            <span className="truncate">{label}</span>
          </span>
        ))}
      </div>
    );
  };

  const renderReplyPreview = (reply, isAdmin) => {
    if (!reply?.id) return null;
    const preview = reply.body || (reply.attachment_count ? 'Photo' : 'Message');
    return (
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        className="mb-1.5 block max-w-full rounded-xl border-l-4 px-2.5 py-1.5 text-left"
        style={{
          background: isAdmin ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.07)',
          borderLeftColor: isAdmin ? 'rgba(255,255,255,0.72)' : '#0084FF',
          color: isAdmin ? '#EAF2FF' : '#334155',
        }}
      >
        <span className="block truncate text-[11px] font-black">
          {reply.sender_role === 'player' ? 'Player' : (reply.sender_name || 'Support')}
        </span>
        <span className="block truncate text-[12px] font-semibold opacity-80">
          {preview}
        </span>
      </button>
    );
  };

  const renderDateSeparator = (label) => label ? (
    <div className="flex justify-center py-5">
      <span
        className="rounded-full px-3.5 py-1 text-[12px] font-semibold"
        style={{
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: '#B7BFCC',
        }}
      >
        {label}
      </span>
    </div>
  ) : null;

  const filteredRooms = sortRooms(rooms).filter(r => {
    if (filterMode === 'online' && !r.player_online) return false;
    if (filterMode === 'favorites' && !(r.is_important || favorites.includes(r.player_id))) return false;
    if (filterMode === 'newly_joined' && !isNewlyJoinedRoom(r)) return false;
    if (normalizeChatSearch(search)) {
      const haystack = `${r.chat_display_name || ''} ${r.player_name || ''} ${r.player_username || ''} ${isAgentPortal ? '' : (r.player_email || '')} ${r.tenant_name || ''} ${r.subject || ''} ${isAgentPortal ? '' : (r.newly_joined_notice || '')} ${r.last_message || ''} ${roomLabels(r).join(' ')}`.toLowerCase();
      if (!haystack.includes(normalizeChatSearch(search))) return false;
    }
    return true;
  });

  const matchingDirectoryPlayers = unmatchedChatPlayers(directorySearch.players, filteredRooms);

  const totalUnread = rooms.reduce((s, r) => s + (r.staff_unread || 0), 0);
  const archiveKey = selectedRoom?.id && selectedRoom?.player_id
    ? `${selectedRoom.id}|${selectedRoom.player_id}` : '';
  const currentArchive = archivedHistory.key === archiveKey
    ? archivedHistory : { messages: [], loading: false, error: '' };
  const visibleArchivedMessages = currentArchive.messages.filter(message => (
    messageMatchesAgentFilter(message) && !messages.some(current => current.id === message.id)
  ));
  useEffect(() => {
    onUnreadCountChange?.(totalUnread);
  }, [onUnreadCountChange, totalUnread]);
  const onlineCount = rooms.filter(r => r.player_online).length;
  const newlyJoinedCount = rooms.filter(isNewlyJoinedRoom).length;
  const selectedRoomAgentName = agentNameForRoom(selectedRoom);
  const activeAgentOption = agentFilter === 'all' ? null : agentOptions.find(option => option.key === agentFilter);
  const activeAgentName = activeAgentOption?.name || '';
  const selectedCustomer = playerDetailsPlayerId === selectedRoom?.player_id
    ? playerDetails?.customer
    : null;
  const selectedRoomIsFavorite = Boolean(
    selectedRoom?.is_important || favorites.includes(selectedRoom?.player_id)
  );
  const latestOwnMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.sender_role !== 'player') return messages[i]?.id || '';
    }
    return '';
  }, [messages]);

  const mobilePanelStyle = isCompactScreen
    ? {
        top: 'var(--staff-chat-viewport-top, 0px)',
        height: 'var(--staff-chat-viewport-height, 100dvh)',
        paddingTop: 'max(env(safe-area-inset-top), 28px)',
      }
    : undefined;
  const roomsPaneShellClass = embedded
    ? `${mobileChatOpen ? 'hidden' : 'flex'} agent-embedded-chat-pane agent-embedded-chat-rooms w-full min-w-0 flex-col overflow-hidden md:flex md:w-80 md:flex-shrink-0`
    : `${mobileChatOpen ? 'hidden' : 'fixed md:static flex'} chat-lobby-panel chat-lobby-rooms left-0 right-0 z-[60] w-full md:z-auto md:flex md:w-80 md:flex-shrink-0 overflow-hidden flex-col md:h-auto`;
  const chatPaneShellClass = embedded
    ? `${mobileChatOpen ? 'flex' : 'hidden'} agent-embedded-chat-pane agent-embedded-chat-conversation w-full min-w-0 flex-col overflow-hidden md:flex md:flex-1`
    : `${mobileChatOpen ? 'fixed md:static flex' : 'hidden'} chat-lobby-panel chat-lobby-conversation left-0 right-0 z-[70] w-full md:z-auto md:flex md:flex-1 overflow-hidden flex-col md:h-auto`;

  const showAdminModeSwitch = !embedded && !isAgentPortal;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="admin-live-chat-shell">
      {showAdminModeSwitch && (
        <div className="flex w-full max-w-md shrink-0 rounded-lg border border-white/10 bg-[#0B0E14] p-1" role="tablist" aria-label="Live chat type">
          <button
            type="button"
            role="tab"
            aria-selected={adminChatMode === 'customer'}
            onClick={() => setAdminChatMode('customer')}
            className={`flex min-h-10 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold transition ${adminChatMode === 'customer' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
          >
            <MessageCircle className="h-4 w-4" /> Customer Chat
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={adminChatMode === 'agent'}
            onClick={() => setAdminChatMode('agent')}
            className={`flex min-h-10 flex-1 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold transition ${adminChatMode === 'agent' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
          >
            <UserPlus className="h-4 w-4" /> Agent Chat
          </button>
        </div>
      )}

      {showAdminModeSwitch && adminChatMode === 'agent' ? (
        <AdminAgentChatPanel />
      ) : (
      <div
        className={`chat-support-layout flex min-h-0 flex-1 gap-0 overflow-hidden md:gap-3 ${embedded ? 'agent-embedded-chat' : ''}`}
        style={{ minHeight: 0 }}
        data-testid="chat-support-page"
        aria-busy={loading ? 'true' : undefined}
        onPointerDownCapture={closeReactionsOnOutsideClick}
      >
      {/* Rooms Sidebar */}
      <div
        className={roomsPaneShellClass}
        style={embedded ? undefined : mobilePanelStyle}
      >
        <div className="agent-embedded-chat-toolbar space-y-2 p-3 pt-2">
          {/* The agent portal names this page in its own header, so the inbox
              only shows a title row where it still carries controls. */}
          {(!isAgentPortal || !wsConnected) && (
            <div className="flex items-center justify-between">
              {!isAgentPortal ? (
                <h3 className="font-semibold flex items-center gap-2 text-sm">
                  <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
                  Chats {totalUnread > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{totalUnread}</span>}
                </h3>
              ) : <span />}
              <div className="flex items-center gap-2">
                {!isAgentPortal && (
                  <button
                    type="button"
                    onClick={() => {
                      setBulkRecallOpen(true);
                      setBulkRecallResult(null);
                    }}
                    className="inline-flex h-8 items-center gap-1.5 rounded-xl border border-[#FDE68A]/70 bg-[#D4AF37] px-3 text-xs font-black text-[#111827] shadow-[0_0_18px_rgba(212,175,55,0.28)] transition hover:bg-[#FDE68A] active:scale-[0.98]"
                    title="Send one message to selected or chatted players"
                    aria-label="Open bulk recall"
                  >
                    <Megaphone className="h-3.5 w-3.5" />
                    Recall
                  </button>
                )}
                {!isAgentPortal && onlineCount > 0 && (
                  <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-semibold text-green-300">
                    {onlineCount} online
                  </span>
                )}
                {/* A healthy socket needs no badge; only a broken one does. */}
                {!wsConnected ? (
                  <div className="flex items-center gap-1 text-[10px] text-red-400">
                    <WifiOff className="w-3 h-3" />
                    Reconnecting...
                  </div>
                ) : !isAgentPortal && (
                  <div className="flex items-center gap-1 text-[10px] text-green-400">
                    <Wifi className="w-3 h-3" />
                    Live
                  </div>
                )}
                {!isAgentPortal && (
                  <button
                    type="button"
                    onClick={openWorkspaceMenu}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-gray-100 transition hover:bg-white/10 md:hidden"
                    aria-label="Open staff workspace menu"
                    title="Open staff workspace menu"
                    data-testid="chat-workspace-menu-button"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          )}
          {isMasterAdmin && tenantOptions.length > 1 && (
            <div className="rounded-xl border border-[#D4AF37]/25 bg-[#0B0E14]/80 p-2" data-testid="master-chat-tenant-picker">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                Select tenant
              </label>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="h-9 w-full rounded-lg border border-white/10 bg-[#111824] px-2 text-xs font-semibold text-white outline-none"
                data-testid="master-chat-tenant-select"
              >
                <option value="" disabled>Choose tenant...</option>
                {tenantOptions.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name || tenant.slug || 'Tenant'}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isMasterAdmin && tenantOptions.length === 1 && (
            <div className="rounded-xl border border-[#D4AF37]/25 bg-[#0B0E14]/80 px-3 py-2" data-testid="master-chat-workspace">
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]">Chat workspace</p>
              <p className="mt-0.5 truncate text-xs font-semibold text-white">
                {tenantOptions[0].name || tenantOptions[0].slug || 'Slots Oceans'}
              </p>
            </div>
          )}
          {isMasterAdmin && (
            <div className="flex rounded-lg border border-white/10 bg-[#0B0E14] p-1" role="group" aria-label="Conversation ownership filter">
              <button
                type="button"
                onClick={() => setAgentConversationMode('all')}
                className={`min-h-8 flex-1 rounded-md px-2 text-[10px] font-bold transition ${agentConversationMode === 'all' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                data-testid="master-chat-all-texts"
              >
                All texts
              </button>
              <button
                type="button"
                onClick={() => setAgentConversationMode('agent_only')}
                className={`min-h-8 flex-1 rounded-md px-2 text-[10px] font-bold transition ${agentConversationMode === 'agent_only' ? 'bg-[#D4AF37] text-[#111827]' : 'text-gray-500 hover:text-gray-300'}`}
                data-testid="master-chat-agent-texts"
              >
                Texted to agents
              </button>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search players..."
              className="agent-embedded-chat-search h-8 border-white/10 bg-[#0B0E14] pl-8 text-xs"
              data-testid="chat-search"
            />
          </div>
          <div className="relative">
            <Filter className="pointer-events-none absolute left-2.5 top-1/2 w-3.5 h-3.5 -translate-y-1/2 text-gray-500" />
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              className="agent-embedded-chat-filters h-8 w-full appearance-none rounded-md border border-white/10 bg-[#0B0E14] pl-8 pr-7 text-[11px] font-medium text-white outline-none"
              data-testid="chat-filter-select"
              aria-label="Filter conversations"
            >
              <option value="all">All clients</option>
              <option value="online">Online</option>
              <option value="favorites">Favorites</option>
              <option value="newly_joined">New clients{newlyJoinedCount > 0 ? ` (${newlyJoinedCount})` : ''}</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 w-3.5 h-3.5 -translate-y-1/2 text-gray-500" />
          </div>
          {agentOptions.length > 1 && (
            <div className="flex gap-1 overflow-x-auto pb-0.5" data-testid="agent-filter-tabs">
              {agentOptions.map(option => {
                const active = agentFilter === option.key;
                const accent = option.key === 'all'
                  ? { bg: 'rgba(212,175,55,0.14)', border: 'rgba(212,175,55,0.45)', color: '#FDE68A' }
                  : agentAccentForName(option.name);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setAgentFilter(option.key)}
                    className={`agent-embedded-chat-agent-filter shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${active ? 'is-active' : ''}`}
                    style={{
                      background: active ? accent.bg : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${active ? accent.border : 'rgba(255,255,255,0.08)'}`,
                      color: active ? accent.color : 'rgba(229,231,235,0.72)',
                    }}
                    data-testid={`agent-filter-${option.key}`}
                  >
                    {option.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          ref={roomsPaneRef}
          className="flex-1 overscroll-contain overflow-y-auto pb-2"
          onTouchStart={handleRoomsTouchStart}
          onTouchEnd={handleRoomsTouchEnd}
        >
          {refreshingRooms && (
            <div className="mx-auto my-2 w-fit rounded-full bg-white/10 px-3 py-1 text-[11px] text-gray-300">
              Fetching latest chats...
            </div>
          )}
          {loading && (
            <div className="agent-embedded-chat-load-state" role="status" data-testid="chat-rooms-loading">
              <div className="agent-embedded-chat-spinner" />
              <strong>Loading your chats</strong>
              <span>Getting your customer conversations ready…</span>
            </div>
          )}
          {!loading && loadError && (
            <div className="agent-embedded-chat-load-state is-error" role="alert" data-testid="chat-rooms-error">
              <strong>Chats are unavailable right now</strong>
              <span>{loadError}</span>
              <button type="button" onClick={retryRoomsNow}>Try again</button>
            </div>
          )}
          {!loading && !loadError && filteredRooms.map(room => {
            const roomAgentName = agentNameForRoom(room);
            const roomIsNewlyJoined = isNewlyJoinedRoom(room);
            const roomLastMessage = roomIsNewlyJoined
              ? (isAgentPortal ? `${visiblePlayerName(room)} joined ${room.tenant_name || 'this team'}` : newlyJoinedPreview(room))
              : (parseAgentTagFromBody(room.last_message || '').body || (room.last_message ? 'Photo'
                : (selectedRoom?.id === room.id && currentArchive.messages.length > 0 ? 'Previous chat history' : 'No messages')));
            const senderLabel = lastMessageSenderLabel(room);
            return (
              <button
                key={room.id}
                onClick={() => openMobileConversation(room)}
                data-testid={`room-${room.id}`}
                className={`agent-embedded-chat-room w-full p-3 text-left transition-all ${
                  room.staff_unread > 0 ? 'is-unread ' : ''
                }${
                  selectedRoom?.id === room.id ? 'is-selected bg-[#D4AF37]/10' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {renderAvatar(resolveMediaUrl(room.player_avatar_url || ''), playerInitial(visiblePlayerName(room), isAgentPortal ? '' : room.player_email), 'w-11 h-11')}
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold">{visiblePlayerName(room)}</span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {roomIsNewlyJoined && (
                          <span className="rounded-full bg-cyan-400/10 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.12em] text-cyan-200">
                            New
                          </span>
                        )}
                        {room.player_online && <span className="h-2.5 w-2.5 rounded-full bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.8)]" title="Online" />}
                        {room.staff_unread > 0 && (
                          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-pulse">{room.staff_unread}</span>
                        )}
                        <div onClick={(e) => toggleFavorite(e, room.player_id)} className={`rounded p-0.5 hover:bg-white/10 cursor-pointer ${(room.is_important || favorites.includes(room.player_id)) ? 'text-[#D4AF37]' : 'text-gray-600'}`} title="Mark important">
                          {(room.is_important || favorites.includes(room.player_id)) ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
                        </div>
                      </div>
                    </div>
                    {!isAgentPortal && room.chat_display_name && realPlayerName(room) !== visiblePlayerName(room) && (
                      <p className="truncate text-[10px] text-gray-500">
                        {realPlayerName(room)}{playerEmail(room) ? ` · ${playerEmail(room)}` : ''}
                      </p>
                    )}
                    {room.staff_unread > 0 ? (
                      <p className="truncate text-[11px] font-semibold text-red-300">{room.staff_unread} unread message{room.staff_unread === 1 ? '' : 's'}</p>
                    ) : (
                      <p className="truncate text-[12px] text-gray-400">
                        {senderLabel ? `${senderLabel}: ${roomLastMessage}` : roomLastMessage}
                      </p>
                    )}
                    {renderRoomLabels(room, 'mt-1')}
                    {renderAgentPill(roomAgentName) && (
                      <div className="mt-1 max-w-full truncate">
                        {renderAgentPill(roomAgentName)}
                      </div>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                      {room.last_message_at && <span>{formatExactTime(room.last_message_at)}</span>}
                      {room.player_online && <span className="text-green-400">Online now</span>}
                      {typingByRoom[room.id] && <span className="text-[#D4AF37]">typing...</span>}
                      {room.staff_unread > 0 && <span className="ml-auto h-2 w-2 rounded-full bg-red-400 shadow-[0_0_10px_rgba(248,113,113,0.75)]" />}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          <ChatPlayerSearch
            {...directorySearch}
            players={matchingDirectoryPlayers}
            onOpen={openSearchPlayer}
            openingPlayerId={openingSearchPlayerId}
          />
          {!loading && !loadError && !directorySearch.loading && !directorySearch.error && filteredRooms.length === 0 && matchingDirectoryPlayers.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{filterMode === 'favorites' ? 'No favorites' : filterMode === 'online' ? 'No online players' : filterMode === 'newly_joined' ? 'No newly joined players' : search ? 'No matches' : isAgentPortal ? 'No players have texted you yet' : 'No active chats yet'}</p>
            </div>
          )}
          {roomPageInfo.total > rooms.length && (
            <div className="border-t border-white/5 p-3 text-center">
              <div className="mb-2 text-[11px] text-gray-500">
                Showing {rooms.length} of {roomPageInfo.total} chats
              </div>
              <Button
                type="button"
                onClick={loadOlderRooms}
                disabled={loadingOlderRooms}
                variant="outline"
                size="sm"
                className="w-full border-white/10 bg-white/5 text-xs text-gray-200 hover:bg-white/10"
              >
                {loadingOlderRooms ? 'Loading older chats...' : 'Load older chats'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div
        className={chatPaneShellClass}
        style={embedded ? undefined : mobilePanelStyle}
      >
        {selectedRoom ? (
          <>
            <div className="agent-embedded-chat-header sticky top-0 z-10 flex shrink-0 items-center justify-between bg-[#152235]/95 px-4 py-3 backdrop-blur">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={closeMobileConversation}
                  className="md:hidden flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white"
                  aria-label="Back to chat list"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                {renderAvatar(resolveMediaUrl(selectedRoom.player_avatar_url || ''), playerInitial(visiblePlayerName(selectedRoom), isAgentPortal ? '' : selectedRoom.player_email), 'w-9 h-9')}
                <div className="min-w-0 flex-1">
                  {editingRoomName ? (
                    <div className="w-[280px] max-w-[calc(100vw-96px)] rounded-2xl border border-white/10 bg-[#07111f] p-2 shadow-xl">
                      <input value={roomNameDraft} onChange={(e) => setRoomNameDraft(e.target.value)} onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRoomDisplayName();
                        if (e.key === 'Escape') closeRoomEditor();
                      }} className="h-8 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm font-semibold text-white outline-none" placeholder="Display name" autoFocus />
                      <div className="mt-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500"><span>Labels</span><span>{roomLabelDrafts.length}/{MAX_ROOM_LABELS}</span></div>
                      <div className="mt-1 flex min-h-7 flex-wrap gap-1">
                        {roomLabelDrafts.length ? roomLabelDrafts.map(label => <button key={label} type="button" onClick={() => setRoomLabelDrafts(prev => prev.filter(item => item !== label))} className="inline-flex items-center gap-1 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-2 py-1 text-[10px] font-black uppercase tracking-[0.08em] text-cyan-100" title="Remove label"><span>{label}</span><X className="h-3 w-3" /></button>) : <span className="text-[11px] text-gray-500">No labels yet</span>}
                      </div>
                      <div className="mt-2 flex gap-1.5"><input value={roomLabelInput} onChange={(e) => setRoomLabelInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRoomLabelDraft(); } if (e.key === 'Escape') closeRoomEditor(); }} maxLength={MAX_ROOM_LABEL_LENGTH} disabled={roomLabelDrafts.length >= MAX_ROOM_LABELS} className="h-8 min-w-0 flex-1 rounded-xl border border-white/10 bg-black/30 px-2 text-xs text-white outline-none disabled:opacity-50" placeholder={roomLabelDrafts.length >= MAX_ROOM_LABELS ? 'Max labels reached' : 'Add label'} /><button type="button" onClick={addRoomLabelDraft} disabled={roomLabelDrafts.length >= MAX_ROOM_LABELS} className="rounded-xl bg-cyan-400/15 px-2 text-xs font-bold text-cyan-100 disabled:opacity-40">Add</button></div>
                      <div className="mt-2 flex gap-1.5"><button type="button" onClick={saveRoomDisplayName} className="flex-1 rounded-xl bg-[#D4AF37] px-3 py-1.5 text-xs font-black text-black">Save</button><button type="button" onClick={closeRoomEditor} className="rounded-xl bg-white/10 px-3 py-1.5 text-xs font-bold text-gray-200">Cancel</button></div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2"><h4 className="truncate font-semibold text-base leading-tight">{visiblePlayerName(selectedRoom)}</h4>{selectedRoom.player_online && <span className="h-2 w-2 rounded-full bg-green-400" title="Online" />}</div>
                      {playerUsernameLabel(selectedRoom) && (
                        <button
                          type="button"
                          onClick={(event) => copyRoomUsername(selectedRoom, event)}
                          className="group mt-0.5 inline-flex max-w-full items-center gap-1.5 text-left text-[11px] font-semibold text-gray-400 hover:text-[#D4AF37]"
                          title="Copy username"
                          data-testid={`chat-copy-username-${selectedRoom.id || 'selected'}`}
                        >
                          <span className="truncate">{playerUsernameLabel(selectedRoom)}</span>
                          <Copy className="h-3 w-3 shrink-0 opacity-70 transition group-hover:opacity-100" />
                        </button>
                      )}
                      {typingByRoom[selectedRoom.id] ? <p className="truncate text-[11px] text-[#D4AF37]">{visiblePlayerName(selectedRoom)} is typing...</p> : <><>{!isAgentPortal && selectedRoom.chat_display_name && realPlayerName(selectedRoom) !== visiblePlayerName(selectedRoom) && <p className="truncate text-[11px] text-gray-300">{realPlayerName(selectedRoom)}</p>}</><p className="truncate text-[11px] text-gray-400">{isAgentPortal ? (selectedRoom.player_online ? 'Online now' : 'Customer') : (playerEmail(selectedRoom) || (selectedRoom.player_online ? 'Online now' : 'No email'))}</p></>}
                      {renderRoomLabels(selectedRoom, 'mt-1')}
                      {renderAgentPill(selectedRoomAgentName) && <div className="mt-1">{renderAgentPill(selectedRoomAgentName)}</div>}
                    </>
                  )}
                </div>
              </div>
              <div className="agent-embedded-chat-header-actions flex items-center gap-2 text-xs">
                {selectedRoom.assigned_staff_name && <span className="hidden sm:flex items-center gap-1 text-[#D4AF37]"><Zap className="w-3.5 h-3.5" /> {selectedRoom.assigned_staff_name}</span>}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setHeaderMenuOpen(open => !open)}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-gray-300 hover:bg-white/10"
                    aria-label="Conversation actions"
                    aria-expanded={headerMenuOpen}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                  {headerMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setHeaderMenuOpen(false)} />
                      <div className="agent-embedded-chat-menu absolute right-0 top-9 z-50 w-44 overflow-hidden rounded-xl bg-white py-1 shadow-xl">
                        <button
                          type="button"
                          onClick={openPlayerDetails}
                          className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
                        >
                          <UserRound className="h-4 w-4" /> Customer overview
                        </button>
                        <button
                          type="button"
                          onClick={() => { setHeaderMenuOpen(false); openRoomEditor(); }}
                          className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
                        >
                          <Pencil className="h-4 w-4" /> Edit name & labels
                        </button>
                        <button
                          type="button"
                          onClick={() => { setHeaderMenuOpen(false); refreshActiveConversation(); }}
                          className="flex h-9 w-full items-center gap-2 px-3 text-left text-[13px] font-semibold text-gray-800 hover:bg-gray-100"
                        >
                          <RefreshCw className={`h-4 w-4 ${refreshingMessages ? 'animate-spin' : ''}`} /> Refresh
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {!isAgentPortal && (
                  <button
                    type="button"
                    onClick={openWorkspaceMenu}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-gray-100 transition hover:bg-white/10 md:hidden"
                    aria-label="Open staff workspace menu"
                    title="Open staff workspace menu"
                    data-testid="chat-conversation-workspace-menu-button"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {agentOptions.length > 1 && (
              <div className="agent-embedded-chat-agent-tabs shrink-0 bg-[#111C2B]/95 px-4 py-2">
                <div className="flex items-center gap-2 overflow-x-auto">
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">Messages</span>
                  {agentOptions.map(option => {
                    const active = agentFilter === option.key;
                    const accent = option.key === 'all'
                      ? { bg: 'rgba(212,175,55,0.16)', border: 'rgba(212,175,55,0.48)', color: '#FDE68A' }
                      : agentAccentForName(option.name);
                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setAgentFilter(option.key)}
                        className={`agent-embedded-chat-agent-filter shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold transition ${active ? 'is-active' : ''}`}
                        style={{
                          background: active ? accent.bg : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${active ? accent.border : 'rgba(255,255,255,0.08)'}`,
                          color: active ? accent.color : 'rgba(229,231,235,0.76)',
                        }}
                      >
                        {option.name}
                      </button>
                    );
                  })}
                </div>
                {activeAgentName && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    Showing only {activeAgentName}. Replies from this screen will be saved under {activeAgentName}.
                  </p>
                )}
              </div>
            )}

            <ScrollArea
              className="agent-embedded-chat-messages min-h-0 flex-1"
              viewportRef={messagesPaneRef}
              viewportClassName="chat-message-viewport overscroll-contain"
              viewportProps={{
                style: { overflowAnchor: 'none', overflowX: 'hidden', maxWidth: '100%' },
                onScroll: updateMessageScrollLock,
                onTouchStart: handleMessagesTouchStart,
                onTouchEnd: handleMessagesTouchEnd,
              }}
              data-testid="chat-messages"
            >
              <div className="chat-message-list flex min-h-full w-full min-w-0 max-w-full flex-col gap-1 px-5 py-3">
              {currentArchive.loading && messages.length === 0 && (
                <div className="text-center text-gray-400 py-4 text-sm" role="status">Loading previous chat history...</div>
              )}
              {currentArchive.error && (
                <div className="mx-auto flex items-center gap-3 py-3 text-sm text-gray-300" role="alert">
                  <span>{currentArchive.error}</span>
                  <button type="button" className="text-amber-300 underline" onClick={() => setArchiveRetry(value => value + 1)}>Retry</button>
                </div>
              )}
              {visibleArchivedMessages.length > 0 && (
                <section aria-label="Previous chat history" className="space-y-2 pb-4">
                  <p className="text-center text-xs font-semibold text-gray-300">Previous chat history · read only</p>
                  {visibleArchivedMessages.map(message => (
                    <div key={`archive-${message.id}`} className={`flex ${message.sender_role === 'player' ? 'justify-start' : 'justify-end'}`}>
                      <div className="max-w-[85%] rounded-2xl bg-white/10 px-3 py-2 text-sm text-white">
                        <p className="whitespace-pre-wrap break-words">{message.body || (message.has_attachment ? 'Photo from previous chat' : '')}</p>
                        <p className="mt-1 text-[11px] text-gray-300">{message.sender_name || (message.sender_role === 'player' ? 'Player' : 'Support')} · {formatExactTime(message.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  {currentArchive.hasMore && <p className="text-center text-xs text-gray-300">Showing the latest archived messages</p>}
                </section>
              )}
              {refreshingMessages && (
                <div className="mx-auto w-fit rounded-full bg-white/10 px-3 py-1 text-[11px] text-gray-300">
                  Fetching latest messages...
                </div>
              )}
              {hasOlderMessages && messages.length > 0 && (
                <div className="flex justify-center py-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={loadOlderMessages}
                    disabled={loadingOlderMessages}
                    className="h-8 rounded-full border-white/10 bg-white/5 px-3 text-[11px] font-semibold text-gray-200 hover:bg-white/10"
                  >
                    {loadingOlderMessages ? 'Loading older...' : 'Load older messages'}
                  </Button>
                </div>
              )}
              {loadingMessages && messages.length === 0 && (
                <div className="text-center text-gray-500 py-8 text-sm">Loading conversation...</div>
              )}
              {!loadingMessages && !currentArchive.loading && !currentArchive.error && messages.length === 0 && visibleArchivedMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8 text-sm">
                  {activeAgentName ? `No messages for ${activeAgentName} yet` : 'No messages yet'}
                </div>
              )}
              {messages.map((msg, index) => {
                const isAdmin = msg.sender_role !== 'player';
                const attachments = msg.attachments || [];
                const parsedBody = parseAgentTagFromBody(msg.body || '');
                const displayBody = parsedBody.body;
                const messageAgentName = msg.target_agent_name || parsedBody.agentName || selectedRoomAgentName;
                const imageOnly = attachments.length > 0 && !displayBody;
                const avatarUrl = avatarForMessage(msg, isAdmin);
                const isLatestOwnMessage = isAdmin && msg.id === latestOwnMessageId;
                const messageStatus = deliveryStatusForMessage(msg, isAdmin);
                const separator = dateSeparatorLabel(msg.created_at, messages[index - 1]?.created_at);
                return (
                  <div key={msg.id} className="contents">
                    {renderDateSeparator(separator)}
                    <Message align={isAdmin ? 'end' : 'start'} className="chat-message-row mt-2.5">
                      <MessageAvatar>
                        {renderMessageAvatar(
                          avatarUrl,
                          isAdmin
                            ? (user?.full_name || user?.username || 'S').charAt(0).toUpperCase()
                            : playerInitial(msg.sender_name, selectedRoom?.player_email)
                        )}
                      </MessageAvatar>
                      <MessageContent className="chat-message-content max-w-[min(82%,640px)]">
                        {visibleTimeId === msg.id && (
                          <p className="mb-1 px-1 text-[10px] text-gray-400">
                            {formatExactTime(msg.created_at)}
                            {messageStatus && ` · ${messageStatus}`}
                            {msg.edited_at && !msg.deleted && ' · Edited'}
                          </p>
                        )}
                        {messageAgentName && (
                          <div className="mb-1 px-1">
                            {renderAgentPill(messageAgentName, isAdmin ? 'Sent as agent' : 'Texted to agent')}
                          </div>
                        )}
                        <BubbleGroup className={isAdmin ? 'items-end' : 'items-start'}>
                          <div className="chat-message-bubble-wrap relative min-w-0 w-fit max-w-full">
                            <Bubble
                              data-chat-bubble
                              variant={imageOnly ? 'image' : (isAdmin ? 'default' : 'muted')}
                              className={`min-w-0 ${msg.id.startsWith('temp_') ? 'opacity-70' : ''}`}
                              onMouseDown={() => beginLongPress(msg.id)}
                              onMouseUp={cancelLongPress}
                              onMouseLeave={cancelLongPress}
                              onPointerDown={(e) => handleMessagePointerDown(e, msg)}
                              onPointerUp={(e) => handleMessagePointerUp(e, msg)}
                              onTouchStart={(e) => handleMessageTouchStart(e, msg)}
                              onTouchEnd={(e) => handleMessageTouchEnd(e, msg)}
                              onClick={(e) => {
                                if (isInteractiveMessageTarget(e.target)) return;
                                if (swipeActivatedRef.current) {
                                  swipeActivatedRef.current = false;
                                  return;
                                }
                                openMessageMenu(msg);
                              }}
                              onContextMenu={(e) => { e.preventDefault(); openMessageMenu(msg); }}
                              style={{
                                WebkitUserSelect: 'none',
                                userSelect: 'none',
                                touchAction: 'pan-y',
                                ...(isAdmin && !imageOnly ? { '--chat-bubble-outgoing': '#0084FF' } : {})
                              }}
                            >
                              <BubbleContent className={imageOnly ? 'p-0' : 'py-1.5'}>
	                              {msg.deleted ? (
	                                <p className="text-[13px] italic opacity-70">Message deleted</p>
	                              ) : displayBody && (
                                  <>
                                    {renderReplyPreview(msg.reply_to, isAdmin)}
                                    <p
                                      className="chat-message-text text-[15px] leading-[1.32] whitespace-pre-wrap"
                                      style={CHAT_MESSAGE_TEXT_STYLE}
                                    >
                                      {renderLinkedMessageText(displayBody, isAdmin)}
                                    </p>
                                  </>
	                              )}
                              {!msg.deleted && !displayBody && renderReplyPreview(msg.reply_to, isAdmin)}
                              {!msg.deleted && attachments.length > 0 && (
                                <div className={`${imageOnly ? '' : 'mt-2'} flex flex-wrap gap-2`}>
                                  {attachments.map(item => {
                                    const imageUrl = resolveMediaUrl(item.url || item.preview_url || (item.path ? `/uploads/${item.path}` : ''));
                                    if (!imageUrl) return null;
                                    return (
                                      <button key={item.id} type="button" onClick={() => openPhotoPreview({ ...item, url: imageUrl })} className="block text-left">
                                        <img src={imageUrl} alt={item.filename || 'chat upload'} className="max-w-[150px] max-h-[150px] object-cover rounded-lg" loading="lazy" decoding="async" />
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              </BubbleContent>
                            </Bubble>
                            {renderReactionBadges(msg, isAdmin)}
                          </div>
                        </BubbleGroup>
	                        {visibleTimeId !== msg.id && ((isLatestOwnMessage && messageStatus) || (msg.edited_at && !msg.deleted)) && (
                          <MessageFooter className="text-gray-400">
                            {isLatestOwnMessage && messageStatus}
                            {msg.edited_at && !msg.deleted && `${isLatestOwnMessage && messageStatus ? ' · ' : ''}Edited`}
                          </MessageFooter>
	                        )}
	                        {renderMessageControls(msg, isAdmin)}
	                        {renderMessageActions(msg, isAdmin)}
	                        {renderQuickReactions(msg)}
                      </MessageContent>
                    </Message>
                  </div>
                );
              })}
              {selectedRoom && typingByRoom[selectedRoom.id] && (
                <Marker role="status" className="justify-start py-1">
                  <MarkerContent className="shimmer bg-white text-gray-700">
                    <span className="font-bold">{visiblePlayerName(selectedRoom)}</span> is typing...
                  </MarkerContent>
                </Marker>
              )}
              <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {replyToMessage && (
              <div className="agent-embedded-chat-reply shrink-0 bg-[#152235]/95 px-4 py-2">
                <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-3 py-2">
                  <Reply className="h-4 w-4 shrink-0 text-[#60A5FA]" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-black uppercase tracking-[0.08em] text-[#9CCBFF]">
                      Replying to {replyToMessage.sender_role === 'player' ? 'player' : (replyToMessage.sender_name || 'support')}
                    </p>
                    <p className="truncate text-xs font-semibold text-gray-200">
                      {replyToMessage.body || (replyToMessage.attachment_count ? 'Photo' : 'Message')}
                    </p>
                  </div>
                  <button type="button" onClick={() => setReplyToMessage(null)} className="h-8 w-8 rounded-full text-gray-300 hover:bg-white/10" aria-label="Cancel reply">
                    <X className="mx-auto h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {photo && (
              <div className="agent-embedded-chat-attachment flex items-center gap-2 px-4 py-2 text-xs text-gray-400">
                <ImageIcon className="w-4 h-4" />
                <span className="flex-1 truncate">{photo.name}</span>
                <button onClick={() => { setPhoto(null); if (photoRef.current) photoRef.current.value = ''; }} aria-label="Remove photo">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {(aiDraftLoading || aiDraft?.draft_text || aiDraft?.status === 'failed') && (
              <div className="agent-embedded-chat-ai shrink-0 bg-[#101A29]/95 px-4 py-2">
                <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 px-3 py-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-cyan-200">
                      <Bot className="h-3.5 w-3.5" />
                      White Room AI shadow draft
                    </div>
                    <button
                      type="button"
                      onClick={() => fetchAiDraft(selectedRoom.id)}
                      className="rounded-full bg-white/5 px-2 py-1 text-[10px] text-gray-300 hover:bg-white/10"
                    >
                      Refresh
                    </button>
                  </div>
                  {aiDraftLoading ? (
                    <p className="text-xs text-gray-400">Thinking in shadow mode...</p>
                  ) : aiDraft?.draft_text ? (
                    <>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-100">{aiDraft.draft_text}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={useAiDraft} className="rounded-full bg-cyan-400 px-3 py-1 text-xs font-bold text-gray-950">Use draft</button>
                        <button type="button" onClick={() => markAiDraft('good')} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-gray-200">Good</button>
                        <button type="button" onClick={() => markAiDraft('bad')} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-gray-200">Bad</button>
                        {aiDraft.feedback_action && <span className="px-2 py-1 text-[11px] text-gray-400">Marked {aiDraft.feedback_action}</span>}
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-amber-200">AI draft not ready yet. It may still be learning or waiting for the API.</p>
                  )}
                </div>
              </div>
            )}
            <div className="agent-embedded-chat-composer shrink-0 bg-[#152235]/95 px-4 py-3 backdrop-blur">
              {emojiOpen && (
                <div className="mb-3 grid max-h-36 w-64 grid-cols-7 gap-0.5 overflow-y-auto rounded-2xl bg-[#F5F5F7] p-1.5 shadow-2xl">
                  {IPHONE_EMOJIS.map((emoji, index) => (
                    <button
                      key={`${emoji}-${index}`}
                      type="button"
                      onClick={() => addEmoji(emoji)}
                      className="h-7 rounded-xl text-[18px] hover:bg-black/5 transition"
                      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Apple Color Emoji", "Segoe UI Emoji", sans-serif' }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              <div className="agent-embedded-chat-composer-inner flex items-end gap-2 rounded-[28px] border border-white/5 bg-[#0B0E14] px-2 py-2">
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={e => setPhoto(e.target.files?.[0] || null)}
                  data-testid="chat-photo"
                  aria-label="Attach image to chat"
                />
                <Button
                  type="button"
                  onClick={() => photoRef.current?.click()}
                  className="bg-white/5 border border-white/10 hover:bg-white/10 text-white w-10 h-10 p-0 rounded-full"
                  data-testid="chat-photo-btn"
                >
                  <Camera className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  onClick={() => setEmojiOpen(open => !open)}
                  className="bg-white/5 border border-white/10 hover:bg-white/10 text-white w-10 h-10 p-0 rounded-full"
                  aria-label="Open emoji picker"
                >
                  <SmilePlus className="w-4 h-4" />
                </Button>
                <textarea
                  ref={composer.attachInput}
                  onChange={handleInputChange}
                  onBlur={stopTypingPulse}
                  onCompositionStart={composer.onCompositionStart}
                  onCompositionEnd={composer.onCompositionEnd}
                  onKeyDown={e => { if (composer.shouldSendOnEnter(e)) { e.preventDefault(); sendMessage(); } }}
                  placeholder={activeAgentName ? `Reply as ${activeAgentName}...` : 'Type a message...'}
                  rows={1}
                  className="min-w-0 min-h-10 max-h-32 flex-1 resize-none rounded-[22px] border-0 bg-transparent px-3 py-2.5 text-base leading-5 shadow-none outline-none placeholder:text-gray-500 focus:outline-none"
                  style={{ color: '#fff', boxShadow: 'none', scrollbarWidth: 'thin' }}
                  data-testid="chat-input"
                />
                <Button
                  onMouseDown={e => e.preventDefault()}
                  onClick={sendMessage}
                  disabled={(!composer.hasText && !photo) || sending}
                  className="chat-send-button bg-[#0084FF] hover:bg-[#0A78E8] text-white w-11 h-10 p-0 rounded-full"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-14 h-14 mx-auto mb-3 opacity-20" />
              <p className="text-base font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Player messages appear here in real-time</p>
            </div>
          </div>
        )}
      </div>

      {isAgentPortal && (
        <aside className="agent-chat-context-rail" aria-label="Selected customer overview">
          {selectedRoom ? (
            <>
              <section className="agent-chat-context-profile">
                <div className="agent-chat-context-avatar">
                  {resolveMediaUrl(selectedRoom.player_avatar_url || '') ? (
                    <img src={resolveMediaUrl(selectedRoom.player_avatar_url || '')} alt="" />
                  ) : (
                    playerInitial(visiblePlayerName(selectedRoom))
                  )}
                  <i className={selectedRoom.player_online ? 'is-online' : ''} />
                </div>
                <h2>{visiblePlayerName(selectedRoom)}</h2>
                <p>{selectedRoom.player_online ? 'Active now' : 'Customer'}</p>
                <button type="button" className="agent-chat-context-copy" onClick={copySelectedPlayerId} title="Copy player ID">
                  <span>{selectedRoom.player_id}</span><Copy />
                </button>
              </section>

              <dl className="agent-chat-context-stats">
                <div>
                  <dt>Customer since</dt>
                  <dd>{formatExactTime(selectedCustomer?.joined_at || selectedRoom.conversation_started_at || selectedRoom.created_at) || '—'}</dd>
                </div>
                <div>
                  <dt>Total transactions</dt>
                  <dd>{detailsLoading ? '…' : Number(selectedCustomer?.deposit_count || 0) + Number(selectedCustomer?.withdrawal_count || 0)}</dd>
                </div>
                <div>
                  <dt>Total deposits</dt>
                  <dd>{detailsLoading ? '…' : fmtMoney(selectedCustomer?.deposits_usd)}</dd>
                </div>
              </dl>

              <button type="button" className="agent-chat-context-primary" onClick={openPlayerDetails}>
                <UserRound /> View customer overview
              </button>

              <section className="agent-chat-context-section">
                <h3>Quick Actions</h3>
                <div className="agent-chat-context-actions">
                  <button type="button" onClick={() => inputRef.current?.focus()}><MessageCircle /> Send message</button>
                  <button type="button" onClick={openRoomEditor}><Pencil /> Edit name &amp; labels</button>
                  <button type="button" onClick={(event) => toggleFavorite(event, selectedRoom.player_id)}>
                    {selectedRoomIsFavorite ? <Star className="is-favorite" /> : <StarOff />}
                    {selectedRoomIsFavorite ? 'Remove important' : 'Mark important'}
                  </button>
                </div>
              </section>

              <section className="agent-chat-context-section agent-chat-context-labels">
                <div><h3>Labels</h3><button type="button" onClick={openRoomEditor}>Edit</button></div>
                <div>
                  {roomLabels(selectedRoom).length ? roomLabels(selectedRoom).map(label => <span key={label}>{label}</span>) : <p>No labels yet</p>}
                </div>
              </section>
            </>
          ) : (
            <div className="agent-chat-context-empty">
              <UserRound />
              <strong>Select a customer</strong>
              <span>Their safe account overview will appear here.</span>
            </div>
          )}
        </aside>
      )}

      {detailsOpen && (
        <div
          className="fixed inset-0 z-[115] flex items-center justify-center p-3 sm:p-5"
          style={{ background: 'rgba(3,8,18,0.78)', backdropFilter: 'blur(14px)' }}
          onClick={() => setDetailsOpen(false)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#111B2A] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate text-lg font-black text-white">{visiblePlayerName(selectedRoom || {})}</h3>
                <p className="truncate text-xs text-gray-400">{isAgentPortal ? (selectedRoom?.player_id || 'Attributed customer') : (playerEmail(selectedRoom || {}) || 'No email on file')}</p>
              </div>
              <button
                type="button"
                onClick={() => setDetailsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-white/10"
                aria-label="Close player details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {detailsLoading ? (
                <p className="py-10 text-center text-sm text-gray-400">Loading player details...</p>
              ) : playerDetails?.customer ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <DetailStat label="Deposits" value={fmtMoney(playerDetails.customer.deposits_usd)} helper={`${playerDetails.customer.deposit_count || 0} payments`} />
                    <DetailStat label="Cashouts" value={fmtMoney(playerDetails.customer.withdrawals_usd)} helper={`${playerDetails.customer.withdrawal_count || 0} payouts`} />
                    <DetailStat label="Net Revenue" value={fmtMoney(playerDetails.customer.net_revenue_usd)} helper="Deposits minus cashouts" />
                    <DetailStat label="Cash Balance" value={fmtMoney(playerDetails.customer.cash_balance)} helper={`${playerDetails.customer.fp_balance || 0} free play`} />
                  </div>
                  <div className="space-y-1.5 rounded-xl border border-white/10 bg-black/20 p-3 text-xs">
                    <DetailRow label="Username" value={playerDetails.customer.username || '—'} />
                    <DetailRow label="Joined" value={formatExactTime(playerDetails.customer.joined_at) || '—'} />
                    <DetailRow label="Came from" value={playerDetails.customer.source === 'referral_tree' ? 'Referral tree' : 'Direct link'} />
                    <DetailRow label="Tracking code" value={playerDetails.customer.tracking_code || '—'} />
                    <DetailRow label="Status" value={playerDetails.customer.disabled ? 'Disabled' : 'Active'} />
                  </div>
                  {(playerDetails.game_accounts || []).length > 0 && (
                    <div>
                      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">Game accounts</p>
                      <div className="space-y-1.5">
                        {playerDetails.game_accounts.map(acc => (
                          <div key={acc.id || acc.game_id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 p-2.5">
                            <span className="min-w-0">
                              <strong className="block truncate text-xs text-white">{acc.game_name}</strong>
                              <small className="block truncate text-[10px] text-gray-500">{acc.provider_username || acc.provider_code || '—'}</small>
                            </span>
                            <strong className="shrink-0 text-xs tabular-nums text-white">{Number(acc.current_balance || 0).toLocaleString()}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5 rounded-xl border border-white/10 bg-black/20 p-3 text-xs">
                  <DetailRow label={isAgentPortal ? 'Username' : 'Name'} value={isAgentPortal ? visiblePlayerName(selectedRoom || {}) : realPlayerName(selectedRoom || {})} />
                  {!isAgentPortal && <DetailRow label="Email" value={playerEmail(selectedRoom || {}) || '—'} />}
                  <DetailRow label="Status" value={selectedRoom?.status || 'open'} />
                  <DetailRow label="Presence" value={selectedRoom?.player_online ? 'Online now' : 'Offline'} />
                  <DetailRow label="Last message" value={formatExactTime(selectedRoom?.last_message_at) || '—'} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {bulkRecallOpen && (
        <div
          className="fixed inset-0 z-[115] flex items-center justify-center p-3 sm:p-5"
          style={{ background: 'rgba(3,8,18,0.78)', backdropFilter: 'blur(14px)' }}
          onClick={() => !bulkRecallSending && setBulkRecallOpen(false)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#111B2A] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <h3 className="text-lg font-black text-white">Bulk Recall</h3>
                <p className="text-xs text-gray-400">
                  Send one message slowly, one player at a time.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !bulkRecallSending && setBulkRecallOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-gray-300 hover:bg-white/10"
                aria-label="Close bulk recall"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto p-4">
              {isMasterAdmin && tenantOptions.length > 1 && (
                <div className="rounded-xl border border-[#D4AF37]/20 bg-[#0B0E14]/70 p-3">
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-[#D4AF37]">Tenant</p>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => {
                      setSelectedTenantId(e.target.value);
                      setBulkRecallResults([]);
                      setBulkRecallSelected([]);
                      setBulkRecallResult(null);
                      setBulkRecallAgentKey('');
                    }}
                    className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm font-semibold text-white outline-none"
                    disabled={bulkRecallSending}
                  >
                    <option value="" disabled>Choose tenant...</option>
                    {tenantOptions.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name || tenant.slug || 'Tenant'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="rounded-xl border border-sky-400/20 bg-sky-400/10 p-3">
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-sky-200">Send from agent</p>
                {bulkRecallAgentOptions.length > 0 ? (
                  <select
                    value={bulkRecallAgentKey}
                    onChange={(e) => setBulkRecallAgentKey(e.target.value)}
                    className="h-10 w-full rounded-lg border border-sky-300/20 bg-[#0B0E14] px-3 text-sm font-semibold text-white outline-none focus:border-sky-300/60"
                    disabled={bulkRecallSending}
                  >
                    {bulkRecallAgentOptions.map(agent => (
                      <option key={agent.key} value={agent.key}>
                        {agent.name}{agent.title ? ` - ${agent.title}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs font-semibold text-sky-100">
                    No tenant agents are configured yet. This recall will send as your admin user.
                  </div>
                )}
                <p className="mt-2 text-[11px] text-sky-100/70">
                  Player inbox delivery is tied to this agent, so the message opens inside the same chat thread.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setBulkRecallMode('selected')}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    bulkRecallMode === 'selected'
                      ? 'border-[#D4AF37]/70 bg-[#D4AF37]/15 text-white'
                      : 'border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.06]'
                  }`}
                  disabled={bulkRecallSending}
                >
                  <span className="block text-sm font-black">Selected players</span>
                  <span className="text-xs text-gray-400">Search username, email, phone, or id.</span>
                </button>
                <button
                  type="button"
                  onClick={() => setBulkRecallMode('favorites')}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    bulkRecallMode === 'favorites'
                      ? 'border-[#D4AF37]/70 bg-[#D4AF37]/15 text-white'
                      : 'border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.06]'
                  }`}
                  disabled={bulkRecallSending}
                >
                  <span className="flex items-center gap-1.5 text-sm font-black">
                    <Star className="h-3.5 w-3.5 fill-current text-[#D4AF37]" /> Favorites only
                  </span>
                  <span className="text-xs text-gray-400">Only starred players with an existing chat ({favorites.length}).</span>
                </button>
                <button
                  type="button"
                  onClick={() => setBulkRecallMode('all_chatted')}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    bulkRecallMode === 'all_chatted'
                      ? 'border-[#D4AF37]/70 bg-[#D4AF37]/15 text-white'
                      : 'border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.06]'
                  }`}
                  disabled={bulkRecallSending}
                >
                  <span className="block text-sm font-black">All chatted customers</span>
                  <span className="text-xs text-gray-400">Everyone with an existing chat in this tenant—no 500-player cutoff.</span>
                </button>
              </div>

              <div>
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">Message</p>
                <textarea
                  value={bulkRecallMessage}
                  onChange={(e) => setBulkRecallMessage(e.target.value)}
                  placeholder="Type recall message..."
                  className="min-h-[120px] w-full resize-y rounded-xl border border-white/10 bg-[#0B0E14] px-3 py-3 text-sm text-white outline-none placeholder:text-gray-600 focus:border-[#D4AF37]/60"
                  maxLength={5000}
                  disabled={bulkRecallSending}
                />
                <div className="mt-1 text-right text-[10px] text-gray-500">{bulkRecallMessage.length}/5000</div>
              </div>

              {bulkRecallMode === 'selected' ? (
                <div className="space-y-3 rounded-xl border border-white/10 bg-[#0B0E14]/60 p-3">
                  <div className="flex gap-2">
                    <div className="relative min-w-0 flex-1">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                      <input
                        value={bulkRecallSearch}
                        onChange={(e) => setBulkRecallSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            searchBulkRecallPlayers();
                          }
                        }}
                        placeholder="Search player..."
                        className="h-10 w-full rounded-lg border border-white/10 bg-black/30 pl-9 pr-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
                        disabled={bulkRecallSending}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={searchBulkRecallPlayers}
                      className="rounded-lg bg-[#D4AF37] px-4 text-sm font-black text-black disabled:opacity-60"
                      disabled={bulkRecallSearching || bulkRecallSending}
                    >
                      {bulkRecallSearching ? 'Searching...' : 'Search'}
                    </button>
                  </div>

                  {bulkRecallSelected.length > 0 && (
                    <div>
                      <p className="mb-2 text-[10px] font-black uppercase tracking-[0.18em] text-gray-500">
                        Selected ({bulkRecallSelected.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {bulkRecallSelected.map(player => (
                          <button
                            key={player.id}
                            type="button"
                            onClick={() => removeBulkRecallPlayer(player.id)}
                            className="rounded-full border border-green-400/25 bg-green-400/10 px-3 py-1 text-xs font-bold text-green-200 hover:bg-green-400/15"
                            disabled={bulkRecallSending}
                            title="Remove"
                          >
                            {bulkRecallPlayerLabel(player)} x
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="max-h-56 overflow-y-auto rounded-lg border border-white/5">
                    {bulkRecallResults.map(player => {
                      const selected = bulkRecallSelected.some(item => item.id === player.id);
                      return (
                        <button
                          key={player.id}
                          type="button"
                          onClick={() => addBulkRecallPlayer(player)}
                          className="flex w-full items-center justify-between gap-3 border-b border-white/5 px-3 py-2 text-left last:border-b-0 hover:bg-white/[0.04]"
                          disabled={selected || bulkRecallSending}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-bold text-white">{bulkRecallPlayerLabel(player)}</span>
                            <span className="block truncate text-[11px] text-gray-500">
                              @{player.username || '-'} {player.email ? `· ${player.email}` : ''} {player.phone ? `· ${player.phone}` : ''}
                            </span>
                          </span>
                          <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black ${selected ? 'bg-green-400/15 text-green-300' : 'bg-white/10 text-gray-300'}`}>
                            {selected ? 'Added' : 'Add'}
                          </span>
                        </button>
                      );
                    })}
                    {!bulkRecallSearching && bulkRecallSearch.trim().length >= 2 && bulkRecallResults.length === 0 && (
                      <div className="px-3 py-5 text-center text-xs text-gray-500">No players found</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`rounded-xl border p-3 text-sm ${
                  bulkRecallMode === 'favorites'
                    ? 'border-[#D4AF37]/25 bg-[#D4AF37]/10 text-yellow-100'
                    : 'border-orange-400/25 bg-orange-400/10 text-orange-100'
                }`}>
                  {bulkRecallMode === 'favorites'
                    ? 'This will send only to players starred as favorites in the selected tenant. The backend checks the current saved favorites before sending.'
                    : 'This will send to every player who already has a chat in the selected tenant. It sends one-by-one with a small delay, so large recalls can take several minutes.'}
                </div>
              )}

              {bulkRecallResult && (
                <div className="grid grid-cols-3 gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                  <div>
                    <p className="text-xl font-black text-green-300">{bulkRecallResult.sent_count || 0}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500">Sent</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-yellow-300">{bulkRecallResult.skipped_count || 0}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500">Skipped</p>
                  </div>
                  <div>
                    <p className="text-xl font-black text-red-300">{bulkRecallResult.failed_count || 0}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500">Failed</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 border-t border-white/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-gray-500">
                {bulkRecallMode === 'selected'
                  ? `${bulkRecallSelected.length} selected`
                  : bulkRecallMode === 'favorites'
                    ? `${favorites.length} favorites in current tenant`
                    : 'All chatted customers in current tenant'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBulkRecallOpen(false)}
                  className="rounded-lg border border-white/10 px-4 py-2 text-sm font-bold text-gray-300 hover:bg-white/5"
                  disabled={bulkRecallSending}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={sendBulkRecall}
                  className="flex items-center justify-center gap-2 rounded-lg bg-[#D4AF37] px-4 py-2 text-sm font-black text-black disabled:opacity-60"
                  disabled={bulkRecallSending}
                >
                  <Send className="h-4 w-4" />
                  {bulkRecallSending ? 'Sending...' : 'Send Recall'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {photoPreview && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: 'rgba(3,8,18,0.72)', backdropFilter: 'blur(12px)' }}
          onClick={closePhotoPreview}
        >
          <div
            className="relative w-full max-w-4xl max-h-[86vh] rounded-2xl p-3"
            style={{ background: '#0F1B2A', border: '1px solid rgba(255,255,255,0.18)', boxShadow: '0 24px 80px rgba(0,0,0,0.48)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closePhotoPreview}
              className="absolute -top-3 -right-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: '#FFFFFF', color: '#111827', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
              aria-label="Close photo preview"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={photoPreview.url}
              alt={photoPreview.filename || 'chat photo preview'}
              className="w-full max-h-[78vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = parseChatDate(dateStr);
  if (!d) return '';
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function fmtMoney(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function DetailStat({ label, value, helper }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">{label}</p>
      <strong className="mt-0.5 block text-base tabular-nums text-white">{value}</strong>
      {helper && <small className="block text-[10px] text-gray-500">{helper}</small>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className="truncate text-right font-semibold text-white">{value}</span>
    </div>
  );
}

function formatExactTime(dateStr) {
  if (!dateStr) return '';
  const date = parseChatDate(dateStr);
  if (!date) return '';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const time = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).format(date);
  if (isToday) return time;
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}
